import React, { useState, useEffect } from 'react';
import BalanceBreakdown from '../components/BalanceBreakdown';
import { Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Wallet, CreditCard, Banknote, ArrowRightLeft, Coins, Smartphone,
  LogOut, Menu, X, TrendingUp, Activity, CheckCircle, Shield, UserCheck, MapPin, Pencil,
  Zap, Send, ArrowUpRight, Landmark, User, SignalHigh, Loader2
} from 'lucide-react';
import RandSign from '@/components/RandSign';
import { QRCodeSVG } from 'qrcode.react';
import { QRWalletCode, QRScanner } from '@/components/QRWallet';
import Insurance from '@/components/Insurance';
import LocationManager from '@/components/LocationManager';
import { clearApiCache } from '@/lib/sw-register';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://pay.mojomoney.africa';
const API = `${BACKEND_URL}/api`;

const MerchantDashboard = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Admin kill switches (App Features tab → GET /api/features). Until the
  // call answers, everything shows; a switched-off entry disappears from the
  // menu and the quick-action grid, and the backend refuses it regardless.
  const [features, setFeatures] = useState({ features: {}, merchant_enabled: true, loaded: false });

  // Phase 0 — the session token now lives in an HttpOnly cookie sent
  // automatically by the browser (axios.defaults.withCredentials = true,
  // set in src/lib/auth-client.js). We gate dashboard access on the
  // presence of the user profile in localStorage (set on login). The
  // legacy `mojo_token` localStorage value is kept null for backwards
  // compat — existing `Bearer ${token}` axios headers send "Bearer null"
  // but the backend prefers the cookie, so calls still authenticate.
  const token = localStorage.getItem('mojo_token');
  const user = JSON.parse(localStorage.getItem('mojo_user') || '{}');
  const isLoggedIn = !!user?.id;

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    fetchWalletData();
    fetchTransactions();
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const res = await axios.get(`${API}/features`, { headers: { Authorization: `Bearer ${token}` } });
      setFeatures({
        features: res.data?.features || {},
        merchant_enabled: res.data?.merchant_enabled !== false,
        loaded: true,
      });
    } catch (_) {
      // Fail open: an unreachable flag endpoint must not hide the portal.
      setFeatures((f) => ({ ...f, loaded: true }));
    }
  };
  const featureOn = (key) => !features.loaded || features.features[key] !== false;

  const fetchWalletData = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallet(response.data);
      // Keep localStorage user profile in sync with DB
      if (response.data?.user_profile) {
        const stored = JSON.parse(localStorage.getItem('mojo_user') || '{}');
        const merged = { ...stored, ...response.data.user_profile };
        localStorage.setItem('mojo_user', JSON.stringify(merged));
      }
    } catch (error) {
      toast.error('Failed to fetch wallet data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/wallet/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions');
    }
  };

  const handleLogout = async () => {
    // Phase 0 — also clear the HttpOnly cookie server-side via /auth/logout.
    // We don't gate UI on the await result; if the server is unreachable the
    // cookie will still expire after 24h.
    try { await axios.post(`${API}/auth/logout`); } catch (_) { /* offline ok */ }
    localStorage.clear();
    toast.success('Logged out successfully');
    navigate('/');
  };

  // `feature` is the admin switch key (backend/feature_flags.py) that hides the entry.
  const menuItems = [
    { path: '/merchant', icon: <Activity />, label: 'Dashboard', feature: 'dashboard' },
    { path: '/merchant/nfc-transfer', icon: <Zap />, label: 'NFC Transfer', feature: 'nfc_transfer' },
    { path: '/merchant/postbank', icon: <Landmark />, label: 'Postbank', feature: 'postbank' },
    { path: '/merchant/money-transfer', icon: <ArrowRightLeft />, label: 'Money Send', feature: 'money_send' },
    { path: '/merchant/card-payment', icon: <CreditCard />, label: 'Card Payment', feature: 'card_payment' },
    { path: '/merchant/airtime-data', icon: <Smartphone />, label: 'Airtime & Data', feature: 'airtime_data' },
    { path: '/merchant/mojo-air', icon: <SignalHigh />, label: 'Mojo Air SIMs', feature: 'mojo_air' },
    { path: '/merchant/funeral-policies', icon: <Shield />, label: 'Funeral Policies', feature: 'funeral' },
    { path: '/merchant/insurance', icon: <Shield />, label: 'Insurance Premium', feature: 'insurance' },
    { path: '/merchant/profile', icon: <User />, label: 'Profile & Settings', feature: 'profile' },
  ].filter((item) => featureOn(item.feature));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#F2B10C] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[#1B0C34]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5F0] via-[#F5EDE4] to-[#F5E8D8]" data-testid="merchant-dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#F2B10C]/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden">
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <img src="/sticker.svg" alt="Mojo Money" className="h-10 w-10" />
            <div>
              <h1 className="text-xl font-bold text-[#1B0C34]">Mojo Money</h1>
              <p className="text-xs text-[#145539]">Merchant Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-[#1B0C34]">{user.full_name}</p>
              <p className="text-xs text-[#1B0C34]/60">{user.email}</p>
            </div>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-[#F2B10C]"
            >
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 flex gap-6">
        {/* Sidebar */}
        <aside className={`
          ${menuOpen ? 'block' : 'hidden'} lg:block
          fixed lg:relative inset-0 lg:inset-auto
          bg-white lg:bg-transparent
          w-64 lg:w-64 shrink-0
          z-40 lg:z-auto
          p-4 lg:p-0
        `}>
          <Card className="bg-white border-[#F2B10C]/20">
            <CardHeader>
              <CardTitle className="text-[#1B0C34]">Menu</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {menuItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[#F2B10C]/10 transition-colors text-[#1B0C34]"
                  onClick={() => setMenuOpen(false)}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              ))}
            </CardContent>
          </Card>
        </aside>

        {/* Main Content */}
        <main className="flex-1">
          {features.loaded && !features.merchant_enabled && (
            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="merchant-features-off">
              Merchant services are temporarily unavailable. Your balance and history still show; transactions will be refused until service is restored.
            </div>
          )}
          <Routes>
            <Route path="/" element={<DashboardHome wallet={wallet} transactions={transactions} featureOn={featureOn} refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/nfc-transfer" element={<NFCTransfer wallet={wallet} refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/postbank" element={<PostbankServices refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/location" element={<LocationManager refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/topup" element={<WalletTopUp refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/payout" element={<CardPayout wallet={wallet} refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/withdraw" element={<CashWithdrawal refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/deposit" element={<CashDeposit refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/card-payment" element={<CardPayment refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/wallet-transfer" element={<WalletTransfer refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/ace-cross-border" element={<ACECrossBorderTransfer refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/money-transfer" element={<MoneyTransfer refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/ace-token" element={<ACEToken wallet={wallet} refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/airtime-data" element={<AirtimeData refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/mojo-air" element={<MojoAirReseller refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/funeral-policies" element={<Insurance walletBalance={wallet?.balance ?? 0} refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/insurance" element={<InsurancePremium walletBalance={wallet?.balance ?? 0} refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/customer-verification" element={<CustomerIDVerification refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
            <Route path="/profile" element={<MerchantProfile wallet={wallet} refreshData={() => { fetchWalletData(); fetchTransactions(); }} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// Merchant Profile & Settings Page
const SA_PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape'
];

const MerchantProfile = ({ wallet, refreshData }) => {
  const token = localStorage.getItem('mojo_token');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState({});
  const [form, setForm] = useState({
    full_name: '', email: '', phone_number: '',
    business_name: '', province: '', business_address: ''
  });

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('mojo_user') || '{}');
    setUserData(u);
    setForm({
      full_name: u.full_name || '',
      email: u.email || '',
      phone_number: u.phone_number || '',
      business_name: wallet?.business_name || u.business_name || '',
      province: wallet?.province || u.province || '',
      business_address: wallet?.location?.address || u.business_address || '',
    });
  }, [wallet]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/merchant/profile`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedUser = { ...userData, ...form };
      localStorage.setItem('mojo_user', JSON.stringify(updatedUser));
      setUserData(updatedUser);
      // The SW caches /wallet/balance — without clearing, the next
      // fetch returns the pre-save user_profile dict and a casual
      // refresh makes it look like the change didn't stick.
      await clearApiCache();
      toast.success('Profile updated successfully');
      setEditing(false);
      if (refreshData) refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const verified = userData.biometric_verified;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <Card>
        <CardContent className="pt-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <User className="w-6 h-6 text-[#1B0C34]" />
              <div>
                <h2 className="text-xl font-bold text-[#1B0C34]">Profile & Settings</h2>
                <p className="text-sm text-[#1B0C34]/60">Manage your account details and business information</p>
              </div>
            </div>
            {!editing && (
              <Button
                onClick={() => setEditing(true)}
                className="bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              >
                <Pencil className="w-4 h-4 mr-2" /> Edit Profile
              </Button>
            )}
          </div>

          {/* Personal Information */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-[#1B0C34] mb-3">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#1B0C34]/60">Full Name</Label>
                {editing
                  ? <Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Full name" className="mt-1" />
                  : <p className="mt-1 text-sm text-[#1B0C34] font-medium">{form.full_name || '—'}</p>
                }
              </div>
              <div>
                <Label className="text-sm text-[#1B0C34]/60">ID Number</Label>
                <Input value={userData.id_number || ''} disabled className="mt-1 bg-gray-50 text-gray-400" />
                <p className="text-xs text-[#1B0C34]/50 mt-1">ID number cannot be changed</p>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-[#1B0C34] mb-3">Business Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#1B0C34]/60">Business Name</Label>
                {editing
                  ? <Input value={form.business_name} onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))} placeholder="Trading name" className="mt-1" />
                  : <p className="mt-1 text-sm text-[#1B0C34] font-medium">{form.business_name || '—'}</p>
                }
              </div>
              <div>
                <Label className="text-sm text-[#1B0C34]/60">Province</Label>
                {editing
                  ? (
                    <select
                      value={form.province}
                      onChange={e => setForm(f => ({ ...f, province: e.target.value }))}
                      className="mt-1 w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-[#145539]"
                    >
                      <option value="">Select Province</option>
                      {SA_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  )
                  : <p className="mt-1 text-sm text-[#1B0C34] font-medium">{form.province || '—'}</p>
                }
              </div>
              <div className="col-span-2">
                <Label className="text-sm text-[#1B0C34]/60">Business Address</Label>
                {editing
                  ? <Input value={form.business_address} onChange={e => setForm(f => ({ ...f, business_address: e.target.value }))} placeholder="Street address" className="mt-1" />
                  : <p className="mt-1 text-sm text-[#1B0C34] font-medium">{form.business_address || '—'}</p>
                }
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-[#1B0C34] mb-3">Contact Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#1B0C34]/60">Email Address</Label>
                {editing
                  ? <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" className="mt-1" />
                  : <p className="mt-1 text-sm text-[#1B0C34] font-medium">{form.email || '—'}</p>
                }
              </div>
              <div>
                <Label className="text-sm text-[#1B0C34]/60">Phone Number</Label>
                {editing
                  ? <Input value={form.phone_number} onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))} placeholder="+27..." className="mt-1" />
                  : <p className="mt-1 text-sm text-[#1B0C34] font-medium">{form.phone_number || '—'}</p>
                }
              </div>
            </div>
          </div>

          {/* Verification Status */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-[#1B0C34] mb-3">Verification Status</h3>
            {verified
              ? (
                <div className="flex items-center gap-2 text-[#145539]">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">ID Verified</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-[#F2B10C]">
                  <Shield className="w-5 h-5" />
                  <span className="text-sm font-medium">ID Not Verified</span>
                </div>
              )
            }
          </div>

          {/* Save / Cancel buttons */}
          {editing && (
            <div className="flex gap-3 pt-2 border-t">
              <Button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard Home Component
const SA_PROVINCES_EDIT = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape'
];

const DashboardHome = ({ wallet, transactions, refreshData, featureOn = () => true }) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('mojo_user') || '{}');
  const verificationStatus = wallet?.verification_status;
  const [showScanner, setShowScanner] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentStep, setPaymentStep] = useState('scan'); // 'scan', 'amount', 'confirm'
  const token = localStorage.getItem('mojo_token');
  const API = `${process.env.REACT_APP_BACKEND_URL || 'https://pay.mojomoney.africa'}/api`;

  // ---- Profile editing ----
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({
    full_name: user.full_name || '',
    phone_number: user.phone_number || '',
    email: user.email || '',
    business_name: wallet?.business_name || '',
    province: wallet?.province || '',
    business_address: wallet?.location?.address || '',
  });

  const openEditProfile = () => {
    setProfileForm({
      full_name: user.full_name || '',
      phone_number: user.phone_number || '',
      email: user.email || '',
      business_name: wallet?.business_name || '',
      province: wallet?.province || '',
      business_address: wallet?.location?.address || '',
    });
    setShowEditProfile(true);
  };

  const handleProfileSave = async () => {
    setEditLoading(true);
    try {
      await axios.put(`${API}/merchant/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update localStorage user object with new values
      const updatedUser = { ...user, ...profileForm };
      localStorage.setItem('mojo_user', JSON.stringify(updatedUser));
      toast.success('Profile updated successfully');
      setShowEditProfile(false);
      if (refreshData) refreshData();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update profile';
      toast.error(msg);
    } finally {
      setEditLoading(false);
    }
  };

  const handleScanComplete = (data) => {
    try {
      let parsedData;
      if (typeof data === 'string') {
        // Could be a raw payment token (MP...), a wallet ID (MW...), or JSON
        const trimmed = data.trim().toUpperCase();
        if (trimmed.startsWith('MP')) {
          // Payment token from customer
          parsedData = { paymentToken: trimmed, type: 'payment_token' };
        } else if (trimmed.startsWith('MW')) {
          // Raw wallet ID
          parsedData = { walletId: trimmed, type: 'wallet' };
        } else {
          parsedData = JSON.parse(data);
        }
      } else if (data.walletId) {
        parsedData = { ...data, type: 'wallet' };
      } else if (data.paymentToken) {
        parsedData = { ...data, type: 'payment_token' };
      } else {
        parsedData = JSON.parse(data.text || data);
      }

      setScannedData(parsedData);

      if (parsedData.type === 'payment_token') {
        // Token already has a fixed amount — skip the amount step
        setPaymentStep('token_confirm');
        toast.success(`Payment token scanned: ${parsedData.paymentToken}`);
      } else {
        setPaymentStep('amount');
        toast.success(`Customer: ${parsedData.name || parsedData.walletId || 'Unknown'}`);
      }
    } catch (error) {
      toast.error('Invalid QR code format');
    }
  };

  const handlePaymentSubmit = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setPaymentStep('confirm');

    try {
      await axios.post(
        `${API}/wallet/transfer`,
        {
          recipient_wallet_id: scannedData.walletId,
          amount: parseFloat(paymentAmount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Payment received successfully!');
      setShowScanner(false);
      setPaymentStep('scan');
      setScannedData(null);
      setPaymentAmount('');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
      setPaymentStep('amount');
    }
  };

  const handleTokenRedeem = async () => {
    setPaymentStep('confirm');
    try {
      const response = await axios.post(
        `${API}/wallet/redeem-token`,
        { payment_token: scannedData.paymentToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`R${response.data.amount?.toFixed(2)} received via payment token!`);
      setShowScanner(false);
      setPaymentStep('scan');
      setScannedData(null);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Token redemption failed');
      setPaymentStep('token_confirm');
    }
  };

  const resetScanner = () => {
    setShowScanner(false);
    setPaymentStep('scan');
    setScannedData(null);
    setPaymentAmount('');
  };
  
  return (
    <div className="space-y-6">
      {/* Verification Status Alert */}
      {verificationStatus && (
        <Card className={`border-2 ${verificationStatus.verified ? 'border-[#145539] bg-[#145539]/5' : 'border-orange-500 bg-orange-50'}`} data-testid="verification-status-card">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {verificationStatus.verified ? (
                  <CheckCircle className="w-8 h-8 text-[#145539]" />
                ) : (
                  <Shield className="w-8 h-8 text-orange-500" />
                )}
                <div>
                  <p className="font-semibold text-[#1B0C34]">
                    {verificationStatus.verified ? 'ID Verified' : 'ID Verification Required'}
                  </p>
                  {!verificationStatus.verified && (
                    <p className="text-sm text-orange-600 mt-1">
                      Complete ID verification to unlock all transactions
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {verificationStatus.verified ? (
                  <span className="px-3 py-1 bg-[#145539] text-white text-xs font-semibold rounded-full">
                    VERIFIED
                  </span>
                ) : (
                  <Button
                    onClick={() => {
                      navigate(`/verify?id_number=${user.id_number || ''}&user_type=merchant`);
                    }}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="complete-verification-btn"
                  >
                    <Shield className="mr-2 w-4 h-4" />
                    Complete ID Verification
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover bg-gradient-to-br from-[#1B0C34] to-[#2D1854] text-white" data-testid="wallet-balance-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardDescription className="text-white/70">Wallet Balance</CardDescription>
                <CardTitle className="text-3xl" data-testid="wallet-balance">R {wallet?.balance?.toFixed(2) || '0.00'}</CardTitle>
                <BalanceBreakdown wallet={wallet} tone="dark" />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowScanner(true)}
                  className="border-[#F2B10C] text-[#F2B10C] hover:bg-[#F2B10C] hover:text-[#1B0C34]"
                  data-testid="scan-qr-btn"
                >
                  <Smartphone className="w-4 h-4" />
                </Button>
                <QRWalletCode 
                  walletId={wallet.wallet_id} 
                  userName={wallet.user_name || user.full_name} 
                  userType="merchant"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <RandSign className="w-12 h-12 opacity-20" />
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-[#F2B10C] to-[#d99a0a] text-[#1B0C34]" data-testid="ace-token-card">
          <CardHeader>
            <CardDescription className="text-[#1B0C34]/70">ACE Token Balance</CardDescription>
            <CardTitle className="text-3xl" data-testid="ace-token-balance">{wallet?.ace_token_balance?.toFixed(2) || '0.00'}</CardTitle>
          </CardHeader>
          <CardContent>
            <Coins className="w-12 h-12 opacity-20" />
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-[#145539] to-[#1e7049] text-white" data-testid="transactions-card">
          <CardHeader>
            <CardDescription className="text-white/70">Total Transactions</CardDescription>
            <CardTitle className="text-3xl" data-testid="transaction-count">{transactions?.length || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <TrendingUp className="w-12 h-12 opacity-20" />
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-[#1B0C34] text-base">Quick Actions</CardTitle>
          <CardDescription>Fast access to common merchant operations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {[
              { path: '/merchant/location', icon: <MapPin className="w-6 h-6 text-[#145539]" />, label: 'Set Location', feature: 'set_location' },
              { path: '/merchant/topup', icon: <RandSign className="w-6 h-6 text-[#145539]" />, label: 'Add Funds', feature: 'add_funds' },
              { path: '/merchant/payout', icon: <Send className="w-6 h-6 text-[#145539]" />, label: 'Payout to Card', feature: 'payout_to_card' },
              { path: '/merchant/customer-verification', icon: <UserCheck className="w-6 h-6 text-[#F2B10C]" />, label: 'Customer Verify', feature: 'customer_verify' },
              { path: '/merchant/ace-token', icon: <Coins className="w-6 h-6 text-[#F2B10C]" />, label: 'ACE Token', feature: 'ace_token' },
              { path: '/merchant/mojo-air', icon: <SignalHigh className="w-6 h-6 text-emerald-600" />, label: 'Sell Mojo Air', feature: 'mojo_air' },
            ].filter(({ feature }) => featureOn(feature)).map(({ path, icon, label }) => (
              <Link key={path} to={path}>
                <button className="w-full flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:border-[#145539] hover:bg-[#145539]/5 transition-colors">
                  {icon}
                  <span className="text-xs text-[#1B0C34] font-medium text-center leading-tight">{label}</span>
                </button>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Merchant Business Information */}
      {wallet?.merchant_id && (
        <Card data-testid="merchant-info-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-[#1B0C34]">Merchant Information</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={openEditProfile}
              className="flex items-center gap-1 text-[#145539] border-[#145539] hover:bg-[#145539]/10"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-[#1B0C34]/60">Merchant ID</p>
                <p className="font-mono font-bold text-[#1B0C34]" data-testid="merchant-id">{wallet.merchant_id}</p>
              </div>
              <div>
                <p className="text-sm text-[#1B0C34]/60">Full Name</p>
                <p className="font-semibold text-[#1B0C34]">{user.full_name || '—'}</p>
              </div>
              <div>
                <p className="text-sm text-[#1B0C34]/60">Email</p>
                <p className="text-[#1B0C34]">{user.email || '—'}</p>
              </div>
              <div>
                <p className="text-sm text-[#1B0C34]/60">Mobile Number</p>
                <p className="text-[#1B0C34]">{user.phone_number || '—'}</p>
              </div>
              {wallet.business_name && (
                <div>
                  <p className="text-sm text-[#1B0C34]/60">Business Name</p>
                  <p className="font-semibold text-[#1B0C34]">{wallet.business_name}</p>
                </div>
              )}
              {wallet.province && (
                <div>
                  <p className="text-sm text-[#1B0C34]/60">Province</p>
                  <p className="text-[#1B0C34]">{wallet.province}</p>
                </div>
              )}
              {wallet.country && (
                <div>
                  <p className="text-sm text-[#1B0C34]/60">Country</p>
                  <p className="text-[#1B0C34]">{wallet.country}</p>
                </div>
              )}
              {wallet.location?.address && (
                <div className="md:col-span-2">
                  <p className="text-sm text-[#1B0C34]/60">Business Address</p>
                  <p className="text-sm text-[#1B0C34]">{wallet.location.address}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1B0C34]">Edit Profile</DialogTitle>
            <DialogDescription>Update your personal and business details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-[#1B0C34]/70">Full Name</Label>
                <Input
                  value={profileForm.full_name}
                  onChange={e => setProfileForm(f => ({ ...f, full_name: e.target.value }))}
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label className="text-[#1B0C34]/70">Email Address</Label>
                <Input
                  type="email"
                  value={profileForm.email}
                  onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label className="text-[#1B0C34]/70">Mobile Number</Label>
                <Input
                  value={profileForm.phone_number}
                  onChange={e => setProfileForm(f => ({ ...f, phone_number: e.target.value }))}
                  placeholder="+27..."
                />
              </div>
              <div>
                <Label className="text-[#1B0C34]/70">Business Name</Label>
                <Input
                  value={profileForm.business_name}
                  onChange={e => setProfileForm(f => ({ ...f, business_name: e.target.value }))}
                  placeholder="Trading name"
                />
              </div>
              <div>
                <Label className="text-[#1B0C34]/70">Province</Label>
                <select
                  value={profileForm.province}
                  onChange={e => setProfileForm(f => ({ ...f, province: e.target.value }))}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-[#145539]"
                >
                  <option value="">Select province</option>
                  {SA_PROVINCES_EDIT.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[#1B0C34]/70">Business Address</Label>
                <Input
                  value={profileForm.business_address}
                  onChange={e => setProfileForm(f => ({ ...f, business_address: e.target.value }))}
                  placeholder="Street address"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleProfileSave}
                disabled={editLoading}
                className="flex-1 bg-[#145539] hover:bg-[#1e7049] text-white"
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowEditProfile(false)}
                disabled={editLoading}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recent Transactions */}
      <Card data-testid="recent-transactions-card">
        <CardHeader>
          <CardTitle className="text-[#1B0C34]">Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {transactions?.slice(0, 10).map((txn, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-[#FAF5F0] rounded-lg" data-testid={`transaction-${index}`}>
                <div>
                  <p className="font-medium text-[#1B0C34]">{txn.description}</p>
                  <p className="text-sm text-[#1B0C34]/60">{txn.transaction_type.replace('_', ' ').toUpperCase()}</p>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${txn.amount >= 0 ? 'text-[#145539]' : 'text-red-600'}`}>
                    {txn.amount >= 0 ? '+' : ''}R {Math.abs(txn.amount).toFixed(2)}
                  </p>
                  {txn.fee > 0 && <p className="text-xs text-[#1B0C34]/60">Fee: R {txn.fee.toFixed(2)}</p>}
                </div>
              </div>
            ))}
            {!transactions || transactions.length === 0 && (
              <p className="text-center text-[#1B0C34]/60 py-8">No transactions yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* QR Scanner Dialog for Payments */}
      <Dialog open={showScanner} onOpenChange={resetScanner}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1B0C34]">Scan Customer QR Code</DialogTitle>
            <DialogDescription>Scan customer wallet to receive payment</DialogDescription>
          </DialogHeader>

          {paymentStep === 'scan' && (
            <div className="space-y-4">
              <QRScanner onScan={handleScanComplete} />
            </div>
          )}

          {paymentStep === 'amount' && scannedData && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-semibold text-green-800">✓ Customer Scanned</p>
                <p className="text-xs text-green-700 mt-1">
                  {scannedData.name || 'Unknown'} - {scannedData.walletId?.substring(0, 10)}...
                </p>
              </div>

              <div>
                <Label htmlFor="payment_amount">Payment Amount (ZAR)</Label>
                <Input
                  id="payment_amount"
                  type="number"
                  step="0.01"
                  placeholder="Enter amount"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  data-testid="scan-payment-amount"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={resetScanner}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePaymentSubmit}
                  className="flex-1 bg-[#145539] hover:bg-[#1e7049] text-white"
                  disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
                  data-testid="confirm-scan-payment"
                >
                  Confirm Payment
                </Button>
              </div>
            </div>
          )}

          {paymentStep === 'token_confirm' && scannedData && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-semibold text-green-800">✓ Payment Token Scanned</p>
                <p className="text-xs font-mono text-green-700 mt-1">{scannedData.paymentToken}</p>
                <p className="text-xs text-green-600 mt-1">The token amount will be transferred to your wallet.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetScanner} className="flex-1">Cancel</Button>
                <Button
                  onClick={handleTokenRedeem}
                  className="flex-1 bg-[#145539] hover:bg-[#1e7049] text-white"
                  data-testid="confirm-token-redeem"
                >
                  Accept Payment
                </Button>
              </div>
            </div>
          )}

          {paymentStep === 'confirm' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F2B10C] mx-auto"></div>
              <p className="mt-4 text-sm text-[#1B0C34]/70">Processing payment...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Wallet Top-Up Component
const WalletTopUp = ({ refreshData }) => {
  const [formData, setFormData] = useState({
    amount: '',
    card_type: 'debit',
    card_number: '',
    card_holder_name: '',
    expiry_month: '',
    expiry_year: '',
    cvv: ''
  });
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const calculateFee = () => {
    if (!formData.amount) return { fee: 0, net: 0 };
    const amount = parseFloat(formData.amount);
    const feeRate = formData.card_type === 'debit' ? 0.02 : 0.035;
    const fee = amount * feeRate;
    const net = amount - fee;
    return { fee, net };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const last4 = formData.card_number.slice(-4);
      
      const response = await axios.post(
        `${API}/wallet/topup`,
        {
          amount: parseFloat(formData.amount),
          card_type: formData.card_type,
          card_number_last4: last4,
          card_holder_name: formData.card_holder_name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Top-up successful! R${response.data.net_credited} added to wallet`);
      setFormData({
        amount: '',
        card_type: 'debit',
        card_number: '',
        card_holder_name: '',
        expiry_month: '',
        expiry_year: '',
        cvv: ''
      });
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Top-up failed');
    } finally {
      setLoading(false);
    }
  };

  const { fee, net } = calculateFee();

  return (
    <Card data-testid="wallet-topup-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]">
          <CreditCard className="inline mr-2" />Add Funds to Wallet
        </CardTitle>
        <CardDescription>Top-up your wallet balance using debit or credit card</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#1B0C34] mb-1">Card Processing Fees</p>
          <p className="text-xs text-[#1B0C34]/70">
            • Debit Card: 2% fee<br/>
            • Credit Card: 3.5% fee<br/>
            • Fee deducted from top-up amount
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="topup_amount">Top-Up Amount (ZAR)</Label>
            <Input
              id="topup_amount"
              type="number"
              step="0.01"
              min="10"
              data-testid="topup-amount-input"
              placeholder="Enter amount"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
            {formData.amount && (
              <div className="mt-2 p-2 bg-[#FAF5F0] rounded text-sm">
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Gross Amount:</span>
                  <span className="font-semibold text-[#1B0C34]">R{parseFloat(formData.amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Processing Fee ({formData.card_type === 'debit' ? '2%' : '3.5%'}):</span>
                  <span className="text-red-600">-R{fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-[#1B0C34]/10 mt-1">
                  <span className="font-semibold text-[#1B0C34]">Net Credited:</span>
                  <span className="font-bold text-[#145539]">R{net.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <Label>Card Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                data-testid="topup-select-debit-btn"
                variant={formData.card_type === 'debit' ? 'default' : 'outline'}
                className={formData.card_type === 'debit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, card_type: 'debit' })}
              >
                Debit (2%)
              </Button>
              <Button
                type="button"
                data-testid="topup-select-credit-btn"
                variant={formData.card_type === 'credit' ? 'default' : 'outline'}
                className={formData.card_type === 'credit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, card_type: 'credit' })}
              >
                Credit (3.5%)
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="card_holder">Card Holder Name</Label>
            <Input
              id="card_holder"
              data-testid="topup-card-holder-input"
              placeholder="Name on card"
              value={formData.card_holder_name}
              onChange={(e) => setFormData({ ...formData, card_holder_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="card_number">Card Number</Label>
            <Input
              id="card_number"
              data-testid="topup-card-number-input"
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              value={formData.card_number}
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                setFormData({ ...formData, card_number: formatted });
              }}
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="expiry_month">Month</Label>
              <Input
                id="expiry_month"
                data-testid="topup-expiry-month-input"
                placeholder="MM"
                maxLength="2"
                value={formData.expiry_month}
                onChange={(e) => setFormData({ ...formData, expiry_month: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="expiry_year">Year</Label>
              <Input
                id="expiry_year"
                data-testid="topup-expiry-year-input"
                placeholder="YY"
                maxLength="2"
                value={formData.expiry_year}
                onChange={(e) => setFormData({ ...formData, expiry_year: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="cvv">CVV</Label>
              <Input
                id="cvv"
                type="password"
                data-testid="topup-cvv-input"
                placeholder="123"
                maxLength="4"
                value={formData.cvv}
                onChange={(e) => setFormData({ ...formData, cvv: e.target.value })}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="submit-topup-btn"
            className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Add Funds to Wallet'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Card Payout Component
const CardPayout = ({ wallet, refreshData }) => {
  const [formData, setFormData] = useState({
    payout_source: 'wallet',
    amount: '',
    ace_amount: '',
    voucher_code: '',
    verification_code: '',
    card_number: '',
    card_holder_name: '',
    expiry_month: '',
    expiry_year: '',
    cvv: ''
  });
  const [loading, setLoading] = useState(false);
  const [aceInfo, setAceInfo] = useState(null);
  const token = localStorage.getItem('mojo_token');

  useEffect(() => {
    fetchACEInfo();
  }, []);

  const fetchACEInfo = async () => {
    try {
      const response = await axios.get(`${API}/ace-token/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAceInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch ACE info');
    }
  };

  const calculateFee = () => {
    if (!formData.amount) return { fee: 0, net: 0 };
    const amount = parseFloat(formData.amount);
    const fee = amount * 0.025; // 2.5%
    const net = amount - fee;
    return { fee, net };
  };

  const calculateACEAmount = () => {
    if (!formData.amount || !aceInfo) return '';
    const amount = parseFloat(formData.amount);
    return (amount / aceInfo.current_value_zar).toFixed(6);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const requestData = {
        amount: parseFloat(formData.amount),
        payout_source: formData.payout_source,
        card_number: formData.card_number.replace(/\s/g, ''),
        expiry_date: `${formData.expiry_month}/${formData.expiry_year}`,
        cvv: formData.cvv,
        card_holder_name: formData.card_holder_name
      };

      // Add source-specific fields
      if (formData.payout_source === 'ace_token') {
        requestData.ace_amount = parseFloat(formData.ace_amount || calculateACEAmount());
      } else if (formData.payout_source === 'voucher') {
        requestData.voucher_code = formData.voucher_code;
        requestData.verification_code = formData.verification_code;
      }

      const response = await axios.post(
        `${API}/card/payout`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Payout successful! R${response.data.net_payout} sent to card (****${response.data.card_last4})`);
      setFormData({
        payout_source: formData.payout_source,
        amount: '',
        ace_amount: '',
        voucher_code: '',
        verification_code: '',
        card_number: '',
        card_holder_name: '',
        expiry_month: '',
        expiry_year: '',
        cvv: ''
      });
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payout failed');
    } finally {
      setLoading(false);
    }
  };

  const { fee, net } = calculateFee();

  return (
    <Card data-testid="card-payout-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]">
          <Banknote className="inline mr-2" />Payout to Card
        </CardTitle>
        <CardDescription>Withdraw from wallet, vouchers, or ACE tokens to your card</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#1B0C34] mb-1">Payout Fee: 2.5%</p>
          <p className="text-xs text-[#1B0C34]/70">
            • Fee deducted from payout amount<br/>
            • Supports wallet balance, ACE tokens, or vouchers<br/>
            • Processed via Traderoute Gateway
          </p>
        </div>

        {/* Balances Display */}
        <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-[#FAF5F0] rounded-lg">
          <div>
            {/* A payout leaves the platform, so it can only draw on money that
                has settled — not on a card top-up that is still clearing. */}
            <p className="text-xs text-[#1B0C34]/60">
              {wallet?.withdrawable_balance != null ? 'Available to cash out' : 'Wallet Balance'}
            </p>
            <p className="text-sm font-bold text-[#1B0C34]" data-testid="payout-available">
              R{Number(wallet?.withdrawable_balance ?? wallet?.balance ?? 0).toFixed(2)}
            </p>
            {Number(wallet?.pending_balance || 0) > 0 && (
              <p className="text-[11px] text-[#1B0C34]/60 mt-0.5">
                R{Number(wallet.pending_balance).toFixed(2)} still clearing, not yet payable
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-[#1B0C34]/60">ACE Balance</p>
            <p className="text-sm font-bold text-[#F2B10C]">{wallet?.ace_token_balance?.toFixed(4) || '0.0000'}</p>
          </div>
          <div>
            <p className="text-xs text-[#1B0C34]/60">ACE Value</p>
            <p className="text-sm font-bold text-[#145539]">R{aceInfo?.current_value_zar || '0'}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Payout Source */}
          <div>
            <Label>Payout Source</Label>
            <div className="grid grid-cols-3 gap-2">
              <Button
                type="button"
                data-testid="select-wallet-source-btn"
                variant={formData.payout_source === 'wallet' ? 'default' : 'outline'}
                className={formData.payout_source === 'wallet' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, payout_source: 'wallet' })}
              >
                Wallet
              </Button>
              <Button
                type="button"
                data-testid="select-ace-source-btn"
                variant={formData.payout_source === 'ace_token' ? 'default' : 'outline'}
                className={formData.payout_source === 'ace_token' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, payout_source: 'ace_token' })}
              >
                ACE Token
              </Button>
              <Button
                type="button"
                data-testid="select-voucher-source-btn"
                variant={formData.payout_source === 'voucher' ? 'default' : 'outline'}
                className={formData.payout_source === 'voucher' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, payout_source: 'voucher' })}
              >
                Voucher
              </Button>
            </div>
          </div>

          {/* Conditional Fields Based on Source */}
          {formData.payout_source === 'voucher' && (
            <>
              <div>
                <Label htmlFor="payout_voucher_code">Voucher Code</Label>
                <Input
                  id="payout_voucher_code"
                  data-testid="payout-voucher-code-input"
                  placeholder="Enter voucher code"
                  value={formData.voucher_code}
                  onChange={(e) => setFormData({ ...formData, voucher_code: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="payout_verification_code">Verification Code</Label>
                <Input
                  id="payout_verification_code"
                  data-testid="payout-verification-code-input"
                  placeholder="4-digit code"
                  maxLength="4"
                  value={formData.verification_code}
                  onChange={(e) => setFormData({ ...formData, verification_code: e.target.value })}
                  required
                />
              </div>
            </>
          )}

          {/* Amount */}
          <div>
            <Label htmlFor="payout_amount">
              {formData.payout_source === 'ace_token' ? 'ACE Amount' : 'Payout Amount (ZAR)'}
            </Label>
            <Input
              id="payout_amount"
              type="number"
              step={formData.payout_source === 'ace_token' ? '0.0001' : '0.01'}
              data-testid="payout-amount-input"
              placeholder={formData.payout_source === 'ace_token' ? 'ACE tokens to convert' : 'ZAR amount'}
              value={formData.payout_source === 'ace_token' ? formData.ace_amount : formData.amount}
              onChange={(e) => {
                if (formData.payout_source === 'ace_token') {
                  const aceAmt = e.target.value;
                  const zarAmt = aceAmt && aceInfo ? (parseFloat(aceAmt) * aceInfo.current_value_zar).toFixed(2) : '';
                  setFormData({ ...formData, ace_amount: aceAmt, amount: zarAmt });
                } else {
                  setFormData({ ...formData, amount: e.target.value });
                }
              }}
              required
            />
            {formData.amount && (
              <div className="mt-2 p-2 bg-[#FAF5F0] rounded text-sm">
                {formData.payout_source === 'ace_token' && (
                  <div className="flex justify-between mb-1">
                    <span className="text-[#1B0C34]/60">ZAR Equivalent:</span>
                    <span className="font-semibold text-[#1B0C34]">R{parseFloat(formData.amount).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Gross Amount:</span>
                  <span className="font-semibold text-[#1B0C34]">R{parseFloat(formData.amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Payout Fee (2.5%):</span>
                  <span className="text-red-600">-R{fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-[#1B0C34]/10 mt-1">
                  <span className="font-semibold text-[#1B0C34]">Net to Card:</span>
                  <span className="font-bold text-[#145539]">R{net.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Card Details */}
          <div>
            <Label htmlFor="payout_card_holder">Card Holder Name</Label>
            <Input
              id="payout_card_holder"
              data-testid="payout-card-holder-input"
              placeholder="Name on card"
              value={formData.card_holder_name}
              onChange={(e) => setFormData({ ...formData, card_holder_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="payout_card_number">Card Number</Label>
            <Input
              id="payout_card_number"
              data-testid="payout-card-number-input"
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              value={formData.card_number}
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                setFormData({ ...formData, card_number: formatted });
              }}
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="payout_expiry_month">Month</Label>
              <Input
                id="payout_expiry_month"
                data-testid="payout-expiry-month-input"
                placeholder="MM"
                maxLength="2"
                value={formData.expiry_month}
                onChange={(e) => setFormData({ ...formData, expiry_month: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="payout_expiry_year">Year</Label>
              <Input
                id="payout_expiry_year"
                data-testid="payout-expiry-year-input"
                placeholder="YY"
                maxLength="2"
                value={formData.expiry_year}
                onChange={(e) => setFormData({ ...formData, expiry_year: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="payout_cvv">CVV</Label>
              <Input
                id="payout_cvv"
                type="password"
                data-testid="payout-cvv-input"
                placeholder="123"
                maxLength="4"
                value={formData.cvv}
                onChange={(e) => setFormData({ ...formData, cvv: e.target.value })}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="submit-payout-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing Payout...' : 'Payout to Card'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Postbank Services Wrapper (tabs between Withdrawal and Deposit)
const PostbankServices = ({ refreshData }) => {
  const [tab, setTab] = useState('withdraw');

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-[#1B0C34] flex items-center gap-2">
            <Landmark className="w-5 h-5" /> Postbank Services
          </CardTitle>
          <CardDescription>Cash withdrawal and deposit services for Postbank Accounts and SRD Grants</CardDescription>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => setTab('withdraw')}
              className={tab === 'withdraw'
                ? 'flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold'
                : 'flex-1 bg-white border border-gray-300 text-[#1B0C34] hover:bg-gray-50'}
            >
              <Banknote className="w-4 h-4 mr-2" /> Cash Withdrawal
            </Button>
            <Button
              type="button"
              onClick={() => setTab('deposit')}
              className={tab === 'deposit'
                ? 'flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold'
                : 'flex-1 bg-white border border-gray-300 text-[#1B0C34] hover:bg-gray-50'}
            >
              <RandSign className="w-4 h-4 mr-2" /> Cash Deposit
            </Button>
          </div>
        </CardContent>
      </Card>

      {tab === 'withdraw'
        ? <CashWithdrawal refreshData={refreshData} />
        : <CashDeposit refreshData={refreshData} />
      }
    </div>
  );
};

// Cash Withdrawal Component (Postbank REST API - JSON-RPC 2.0)
const CashWithdrawal = ({ refreshData }) => {
  const [step, setStep] = useState('search'); // 'search', 'confirm', 'success'
  const [formData, setFormData] = useState({ 
    national_id: '',
    phone_number: '',
    account_number: '',
    amount: ''
  });
  const [customer, setCustomer] = useState(null);
  const [transferId, setTransferId] = useState(null);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const token = localStorage.getItem('mojo_token');

  const postbankAccounts = Array.isArray(customer) ? customer : (customer?.accounts || []);
  const getAccBalance = (acc) => {
    if (acc.balance?.available?.amount) return parseFloat(acc.balance.available.amount);
    if (typeof acc.balance === 'number') return acc.balance;
    return 0;
  };
  const postbankTotalBalance = postbankAccounts.length > 0
    ? postbankAccounts.reduce((sum, acc) => sum + getAccBalance(acc), 0)
    : null;

  const handleCustomerSearch = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/customer-search`,
        { 
          national_id: formData.national_id,
          phone_number: formData.phone_number
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCustomer(response.data.customer);
        toast.success('Customer found! Select account and enter amount.');
        setStep('confirm');
      } else {
        toast.error(response.data.error || 'Customer not found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Customer search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateWithdrawal = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/withdraw-init`,
        { 
          account_number: formData.account_number,
          amount: parseFloat(formData.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTransferId(response.data.transfer_id);
        toast.success(response.data.message || 'OTP sent to customer mobile');
      } else {
        toast.error(response.data.error || 'Withdrawal initiation failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal initiation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmWithdrawal = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/withdraw-confirm`,
        { 
          transfer_id: transferId,
          otp: otp,
          amount: parseFloat(formData.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setResult(response.data);
        setStep('success');
        toast.success('Withdrawal successful!');
        refreshData();
      } else {
        toast.error(response.data.error || 'OTP verification failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewTransaction = () => {
    setStep('search');
    setFormData({ national_id: '', phone_number: '', account_number: '', amount: '' });
    setCustomer(null);
    setTransferId(null);
    setOtp('');
    setResult(null);
  };

  return (
    <Card data-testid="cash-withdrawal-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><Banknote className="inline mr-2" />Postbank SRD Cash Withdrawal</CardTitle>
        <CardDescription>Withdraw cash from customer Postbank account (Max R370)</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#145539] mb-1">💰 Merchant Commission: R1.25 per transaction</p>
          <p className="text-xs text-[#1B0C34]/70">
            You earn R1.25 commission on every successful withdrawal
          </p>
        </div>

        {step === 'search' && (
          <form onSubmit={handleCustomerSearch} className="space-y-4">
            <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
              <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 1: Customer Search</p>
              <p className="text-xs text-[#1B0C34]/70">
                Enter customer's National ID and Phone Number to search Postbank records
              </p>
            </div>
            
            <div>
              <Label htmlFor="national_id">National ID Number</Label>
              <Input
                id="national_id"
                data-testid="national-id-input"
                placeholder="13-digit SA ID"
                maxLength={13}
                value={formData.national_id}
                onChange={(e) => setFormData({ ...formData, national_id: e.target.value.replace(/\D/g, '') })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="phone_number">Phone Number</Label>
              <Input
                id="phone_number"
                data-testid="phone-number-input"
                placeholder="+27769584862"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                required
              />
              <p className="text-xs text-[#1B0C34]/60 mt-1">Format: +27XXXXXXXXX</p>
            </div>
            
            <Button
              type="submit"
              data-testid="search-customer-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search Customer'}
            </Button>
          </form>
        )}

        {step === 'confirm' && customer && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-semibold text-green-800 mb-2">✓ Customer Found</p>
              {(customer?.firstName || customer?.lastName) && (
                <p className="text-xs text-green-700">{customer.firstName} {customer.lastName}</p>
              )}
              {customer?.phoneNumber && (
                <p className="text-xs text-green-700">Phone: {customer.phoneNumber}</p>
              )}
              {postbankTotalBalance !== null && postbankTotalBalance > 0 && (
                <p className="text-xs text-green-700 mt-1 font-semibold">
                  Total Postbank Balance: R {postbankTotalBalance.toFixed(2)}
                </p>
              )}
            </div>

            {!transferId ? (
              <form onSubmit={handleInitiateWithdrawal} className="space-y-4">
                <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
                  <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 2: Initiate Withdrawal</p>
                  <p className="text-xs text-[#1B0C34]/70">
                    Select account and enter amount. OTP will be sent to customer.
                  </p>
                </div>

                {postbankAccounts.length > 0 && (
                  <div>
                    <Label htmlFor="account_select">Select Account</Label>
                    <select
                      id="account_select"
                      data-testid="account-select"
                      className="w-full p-2 border rounded-md"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      required
                    >
                      <option value="">-- Select Account --</option>
                      {postbankAccounts.map((acc, idx) => {
                        const bal = getAccBalance(acc);
                        return (
                          <option key={idx} value={acc.accountNumber || acc.number}>
                            {acc.accountName || acc.alias || acc.type} - {acc.accountNumber || acc.number}
                            {bal > 0 ? ` (R ${bal.toFixed(2)})` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="amount">Withdrawal Amount (ZAR)</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    max="370"
                    data-testid="withdrawal-amount-input"
                    placeholder="Max R370"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                  <p className="text-xs text-[#1B0C34]/60 mt-1">Maximum R370 per SRD specification</p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep('search')}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    data-testid="initiate-withdrawal-btn"
                    className="flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                    disabled={loading}
                  >
                    {loading ? 'Sending OTP...' : 'Send OTP'}
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleConfirmWithdrawal} className="space-y-4">
                <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
                  <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 3: Confirm with OTP</p>
                  <p className="text-xs text-[#1B0C34]/70">
                    Enter the OTP sent to customer's mobile number
                  </p>
                </div>

                <div>
                  <Label htmlFor="otp">OTP Code</Label>
                  <Input
                    id="otp"
                    data-testid="otp-input"
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    required
                  />
                  <p className="text-xs text-[#1B0C34]/60 mt-1">6-digit code from customer</p>
                </div>
                
                <Button
                  type="submit"
                  data-testid="confirm-withdrawal-btn"
                  className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Confirm Withdrawal'}
                </Button>
              </form>
            )}
          </div>
        )}

        {step === 'success' && result && (
          <div className="space-y-4" data-testid="withdrawal-success">
            <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-2" />
              <h3 className="text-2xl font-bold mb-4">Withdrawal Successful!</h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Amount Withdrawn:</span>
                  <span className="font-bold">R{result.amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Your Commission:</span>
                  <span className="font-bold text-[#F2B10C]">R{result.commission?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span>Total Credited:</span>
                  <span className="font-bold text-[#F2B10C]">R{((result.amount || 0) + (result.commission || 0)).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Button
              onClick={handleNewTransaction}
              data-testid="new-withdrawal-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            >
              New Transaction
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Card Payment Component
const CardPayment = ({ refreshData }) => {
  const [formData, setFormData] = useState({ 
    amount: '', 
    card_number: '', 
    expiry_date: '', 
    cvv: '', 
    card_type: 'debit', 
    description: '' 
  });
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/payment/card`,
        { 
          amount: parseFloat(formData.amount),
          card_number: formData.card_number,
          expiry_date: formData.expiry_date,
          cvv: formData.cvv,
          card_type: formData.card_type,
          description: formData.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Payment successful! Fee: ${response.data.fee_rate} • Net: R${response.data.net_amount}`);
      setFormData({ amount: '', card_number: '', expiry_date: '', cvv: '', card_type: 'debit', description: '' });
      refreshData();
    } catch (error) {
      // Handle validation errors properly
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        // Handle Pydantic validation errors
        toast.error(errorMsg.map(err => err.msg).join(', '));
      } else if (errorMsg && typeof errorMsg === 'object') {
        toast.error(JSON.stringify(errorMsg));
      } else {
        toast.error('Payment failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="card-payment-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><CreditCard className="inline mr-2" />Card Payment</CardTitle>
        <CardDescription>Manual Card payments</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#1B0C34] mb-1">Fee Structure</p>
          <p className="text-xs text-[#1B0C34]/70">
            • Debit Card: 2% processing fee<br/>
            • Credit Card: 3.5% processing fee
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Card Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                data-testid="select-debit-btn"
                variant={formData.card_type === 'debit' ? 'default' : 'outline'}
                className={formData.card_type === 'debit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, card_type: 'debit' })}
              >
                Debit (2%)
              </Button>
              <Button
                type="button"
                data-testid="select-credit-btn"
                variant={formData.card_type === 'credit' ? 'default' : 'outline'}
                className={formData.card_type === 'credit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, card_type: 'credit' })}
              >
                Credit (3.5%)
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="card_number">Card Number</Label>
            <Input
              id="card_number"
              data-testid="card-number-input"
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              value={formData.card_number}
              onChange={(e) => setFormData({ ...formData, card_number: e.target.value.replace(/\s/g, '') })}
              required
            />
            <p className="text-xs text-[#1B0C34]/60 mt-1">16-19 digit card number</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="expiry_date">Expiry Date</Label>
              <Input
                id="expiry_date"
                data-testid="expiry-date-input"
                placeholder="MM/YY"
                maxLength="5"
                value={formData.expiry_date}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, '');
                  if (value.length >= 2) {
                    value = value.slice(0, 2) + '/' + value.slice(2, 4);
                  }
                  setFormData({ ...formData, expiry_date: value });
                }}
                required
              />
            </div>
            <div>
              <Label htmlFor="cvv">CVV</Label>
              <Input
                id="cvv"
                data-testid="cvv-input"
                type="password"
                placeholder="123"
                maxLength="4"
                value={formData.cvv}
                onChange={(e) => setFormData({ ...formData, cvv: e.target.value.replace(/\D/g, '') })}
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="payment_amount">Amount (ZAR)</Label>
            <Input
              id="payment_amount"
              type="number"
              step="0.01"
              data-testid="card-payment-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
            {formData.amount && (
              <p className="text-xs text-[#1B0C34]/60 mt-1">
                Fee: R{(parseFloat(formData.amount) * (formData.card_type === 'debit' ? 0.02 : 0.035)).toFixed(2)} • 
                Net: R{(parseFloat(formData.amount) * (formData.card_type === 'debit' ? 0.98 : 0.965)).toFixed(2)}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              data-testid="payment-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="submit-card-payment-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Process Payment'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Cash Deposit Component (Postbank REST API - JSON-RPC 2.0)
const CashDeposit = ({ refreshData }) => {
  const [step, setStep] = useState('search'); // 'search', 'confirm', 'success'
  const [formData, setFormData] = useState({ 
    national_id: '',
    phone_number: '',
    account_number: '',
    amount: ''
  });
  const [customer, setCustomer] = useState(null);
  const [depositId, setDepositId] = useState(null);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const token = localStorage.getItem('mojo_token');

  const postbankAccounts = Array.isArray(customer) ? customer : (customer?.accounts || []);
  const getAccBalance = (acc) => {
    if (acc.balance?.available?.amount) return parseFloat(acc.balance.available.amount);
    if (typeof acc.balance === 'number') return acc.balance;
    return 0;
  };
  const postbankTotalBalance = postbankAccounts.length > 0
    ? postbankAccounts.reduce((sum, acc) => sum + getAccBalance(acc), 0)
    : null;

  const handleCustomerSearch = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/customer-search`,
        { 
          national_id: formData.national_id,
          phone_number: formData.phone_number
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCustomer(response.data.customer);
        toast.success('Customer found! Select account and enter amount.');
        setStep('confirm');
      } else {
        toast.error(response.data.error || 'Customer not found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Customer search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateDeposit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/deposit-init`,
        { 
          account_number: formData.account_number,
          amount: parseFloat(formData.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setDepositId(response.data.deposit_id);
        toast.success(response.data.message || 'OTP sent to customer mobile');
      } else {
        toast.error(response.data.error || 'Deposit initiation failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deposit initiation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeposit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/deposit-confirm`,
        { 
          deposit_id: depositId,
          otp: otp,
          amount: parseFloat(formData.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setResult(response.data);
        setStep('success');
        toast.success('Deposit successful!');
        refreshData();
      } else {
        toast.error(response.data.error || 'OTP verification failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deposit confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewTransaction = () => {
    setStep('search');
    setFormData({ national_id: '', phone_number: '', account_number: '', amount: '' });
    setCustomer(null);
    setDepositId(null);
    setOtp('');
    setResult(null);
  };

  return (
    <Card data-testid="cash-deposit-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><Banknote className="inline mr-2" />Postbank Cash Deposit</CardTitle>
        <CardDescription>Deposit cash into customer Postbank account</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#145539] mb-1">💰 Merchant Commission: R1.25 per transaction</p>
          <p className="text-xs text-[#1B0C34]/70">
            You earn R1.25 commission for every cash deposit. Cash will be deducted from your wallet.
          </p>
        </div>

        {step === 'search' && (
          <form onSubmit={handleCustomerSearch} className="space-y-4">
            <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
              <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 1: Customer Search</p>
              <p className="text-xs text-[#1B0C34]/70">
                Enter customer National ID and Phone Number to search Postbank records
              </p>
            </div>
            
            <div>
              <Label htmlFor="dep_national_id">National ID Number</Label>
              <Input
                id="dep_national_id"
                data-testid="deposit-national-id-input"
                placeholder="13-digit SA ID"
                maxLength={13}
                value={formData.national_id}
                onChange={(e) => setFormData({ ...formData, national_id: e.target.value.replace(/\D/g, '') })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="dep_phone_number">Phone Number</Label>
              <Input
                id="dep_phone_number"
                data-testid="deposit-phone-number-input"
                placeholder="+27769584862"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                required
              />
              <p className="text-xs text-[#1B0C34]/60 mt-1">Format: +27XXXXXXXXX</p>
            </div>
            
            <Button
              type="submit"
              data-testid="search-customer-deposit-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search Customer'}
            </Button>
          </form>
        )}

        {step === 'confirm' && customer && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-semibold text-green-800 mb-2">✓ Customer Found</p>
              {(customer?.firstName || customer?.lastName) && (
                <p className="text-xs text-green-700">{customer.firstName} {customer.lastName}</p>
              )}
              {customer?.phoneNumber && (
                <p className="text-xs text-green-700">Phone: {customer.phoneNumber}</p>
              )}
              {postbankTotalBalance !== null && postbankTotalBalance > 0 && (
                <p className="text-xs text-green-700 mt-1 font-semibold">
                  Total Postbank Balance: R {postbankTotalBalance.toFixed(2)}
                </p>
              )}
            </div>

            {!depositId ? (
              <form onSubmit={handleInitiateDeposit} className="space-y-4">
                <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
                  <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 2: Initiate Deposit</p>
                  <p className="text-xs text-[#1B0C34]/70">
                    Select account and enter amount. OTP will be sent to customer.
                  </p>
                </div>

                {Array.isArray(customer) && customer.length > 0 && (
                  <div>
                    <Label htmlFor="deposit_account_select">Select Account</Label>
                    <select
                      id="deposit_account_select"
                      data-testid="deposit-account-select"
                      className="w-full p-2 border rounded-md"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      required
                    >
                      <option value="">-- Select Account --</option>
                      {customer.map((acc, idx) => (
                        <option key={idx} value={acc.number}>
                          {acc.alias || acc.type} - {acc.number}
                          {acc.balance != null ? ` (R ${acc.balance.toFixed(2)})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="deposit_amount">Deposit Amount (ZAR)</Label>
                  <Input
                    id="deposit_amount"
                    type="number"
                    step="0.01"
                    data-testid="deposit-amount-input"
                    placeholder="Enter amount"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                  <p className="text-xs text-[#1B0C34]/60 mt-1">Cash will be deducted from your wallet</p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep('search')}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    data-testid="initiate-deposit-btn"
                    className="flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                    disabled={loading}
                  >
                    {loading ? 'Sending OTP...' : 'Send OTP'}
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleConfirmDeposit} className="space-y-4">
                <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
                  <p className="text-sm font-medium text-[#1B0C34] mb-1">Step 3: Confirm with OTP</p>
                  <p className="text-xs text-[#1B0C34]/70">
                    Enter the OTP sent to customer mobile number
                  </p>
                </div>

                <div>
                  <Label htmlFor="deposit_otp">OTP Code</Label>
                  <Input
                    id="deposit_otp"
                    data-testid="deposit-otp-input"
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    required
                  />
                  <p className="text-xs text-[#1B0C34]/60 mt-1">6-digit code from customer</p>
                </div>
                
                <Button
                  type="submit"
                  data-testid="confirm-deposit-btn"
                  className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Confirm Deposit'}
                </Button>
              </form>
            )}
          </div>
        )}

        {step === 'success' && result && (
          <div className="space-y-4" data-testid="deposit-success">
            <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-2" />
              <h3 className="text-2xl font-bold mb-4">Deposit Successful!</h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Amount Deposited:</span>
                  <span className="font-bold">R{result.amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Your Commission:</span>
                  <span className="font-bold text-[#F2B10C]">R{result.commission?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Net Deducted:</span>
                  <span className="font-bold">R{result.net_deducted?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span>New Balance Impact:</span>
                  <span className="font-bold text-red-300">-R{result.net_deducted?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Button
              onClick={handleNewTransaction}
              data-testid="new-deposit-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            >
              New Transaction
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// NFC Transfer Component
const NFCTransfer = ({ wallet, refreshData }) => {
  const [mode, setMode] = useState('send');
  const [amount, setAmount] = useState('');
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redeemInput, setRedeemInput] = useState('');
  const [redeemResult, setRedeemResult] = useState(null);
  const authToken = localStorage.getItem('mojo_token');

  const handleGenerateToken = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { toast.error('Please enter a valid amount'); return; }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/wallet/generate-payment-token`,
        { amount: parseFloat(amount), token_source: 'wallet' },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setToken(response.data);
      toast.success('Transfer token generated — FREE transfer!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate token');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    const code = redeemInput.trim().toUpperCase();
    if (!code) { toast.error('Please enter or paste a token'); return; }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/wallet/redeem-token`,
        { payment_token: code },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setRedeemResult(response.data);
      toast.success(`R${response.data.amount?.toFixed(2)} received successfully!`);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Token redemption failed');
    } finally {
      setLoading(false);
    }
  };

  const qrValue = token ? JSON.stringify({
    type: 'mojo_wallet_transfer',
    token: token.payment_token,
    amount: token.amount,
    app: 'MojoMoney'
  }) : '';
  const expiresMinutes = token ? Math.floor((token.expires_in || 300) / 60) : 5;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-[#1B0C34]">NFC Wallet Transfer</h1>

      <Card>
        <CardContent className="p-0">
          {/* Green header banner */}
          <div className="flex items-center gap-3 p-4 bg-[#145539] text-white rounded-t-lg">
            <Zap className="w-5 h-5 shrink-0" />
            <div>
              <p className="font-semibold text-sm">NFC Wallet Transfer</p>
              <p className="text-xs opacity-80">FREE instant wallet-to-wallet transfers via NFC or QR code</p>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Mode toggle */}
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={() => { setMode('send'); setToken(null); setAmount(''); setRedeemResult(null); }}
                className={mode === 'send'
                  ? 'flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold'
                  : 'flex-1 bg-white border border-gray-300 text-[#1B0C34] hover:bg-gray-50'}
              >
                <Send className="w-4 h-4 mr-2" /> Send Money
              </Button>
              <Button
                type="button"
                onClick={() => { setMode('receive'); setToken(null); setAmount(''); setRedeemResult(null); }}
                className={mode === 'receive'
                  ? 'flex-1 bg-[#145539] hover:bg-[#1e7049] text-white font-semibold'
                  : 'flex-1 bg-white border border-gray-300 text-[#1B0C34] hover:bg-gray-50'}
              >
                <ArrowUpRight className="w-4 h-4 mr-2" /> Receive Money
              </Button>
            </div>

            {/* Send mode */}
            {mode === 'send' && (
              <>
                {!token ? (
                  <form onSubmit={handleGenerateToken} className="space-y-4">
                    <div>
                      <Label htmlFor="nfc_amount">Amount to Send (R)</Label>
                      <Input
                        id="nfc_amount"
                        type="number"
                        step="0.01"
                        min="1"
                        placeholder="Enter amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        required
                        className="text-lg"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading || !amount}
                      className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                      size="lg"
                    >
                      {loading ? 'Generating...' : <><Zap className="mr-2 w-4 h-4" /> Generate Transfer Token</>}
                    </Button>
                    <p className="text-xs text-center text-[#1B0C34]/50">Generate a secure token for the receiver to scan</p>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-[#145539]/10 border border-[#145539]/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="w-5 h-5 text-[#145539]" />
                        <p className="font-semibold text-[#145539]">Transfer Token Generated</p>
                      </div>
                      <div className="space-y-1 text-sm text-[#1B0C34]">
                        <p><span className="font-semibold">Amount:</span> R{token.amount?.toFixed(2)}</p>
                        <p><span className="font-semibold">Token ID:</span> <span className="font-mono">{token.payment_token}</span></p>
                        <p className="flex items-center gap-1">
                          <span className="font-semibold">Expires in:</span> {expiresMinutes} minutes
                        </p>
                      </div>
                    </div>
                    <div className="border-2 border-dashed border-[#F2B10C] rounded-lg p-4 flex flex-col items-center gap-3">
                      <QRCodeSVG value={qrValue} size={200} level="M" includeMargin={true} />
                      <p className="text-xs text-center text-[#1B0C34]/60">
                        Receiver scans this QR code to receive R{token.amount?.toFixed(2)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setToken(null); setAmount(''); }}
                      className="w-full"
                    >
                      Generate Another Token
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Receive mode */}
            {mode === 'receive' && (
              <>
                {!redeemResult ? (
                  <form onSubmit={handleRedeem} className="space-y-4">
                    <div>
                      <Label htmlFor="nfc_redeem_input">Transfer Token Data</Label>
                      <textarea
                        id="nfc_redeem_input"
                        rows={4}
                        placeholder={`Paste the token data here or scan QR code (format: {"type":"mojo_wallet_transfer",...})`}
                        value={redeemInput}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.trim().startsWith('{')) {
                            try {
                              const parsed = JSON.parse(val.trim());
                              if (parsed.token) { setRedeemInput(parsed.token); return; }
                            } catch { /* not valid JSON yet */ }
                          }
                          setRedeemInput(val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12) || val);
                        }}
                        className="w-full border border-[#F2B10C] rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-[#F2B10C] resize-y min-h-[80px]"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading || !redeemInput.trim()}
                      className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
                      size="lg"
                    >
                      {loading ? 'Processing...' : <><ArrowUpRight className="mr-2 w-4 h-4" /> Receive Money</>}
                    </Button>
                    <p className="text-xs text-center text-[#1B0C34]/50">Scan the sender's QR code or paste the token data above</p>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-[#145539]/10 border border-[#145539]/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="w-5 h-5 text-[#145539]" />
                        <p className="font-semibold text-[#145539]">Money Received!</p>
                      </div>
                      <p className="text-2xl font-bold text-[#1B0C34]">R {redeemResult.amount?.toFixed(2)}</p>
                      <p className="text-xs text-[#1B0C34]/60 mt-1">Added to your wallet — FREE transfer</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setRedeemResult(null); setRedeemInput(''); }}
                      className="w-full"
                    >
                      Receive Another
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Wallet Transfer Component
const WalletTransfer = ({ refreshData }) => {
  const [formData, setFormData] = useState({ recipient_wallet_id: '', amount: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [stepupInfo, setStepupInfo] = useState(null);   // { required, method, biometric_fallback_otp }
  const [stepupChecking, setStepupChecking] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [biometricStep, setBiometricStep] = useState(false);
  const token = localStorage.getItem('mojo_token');

  // Check step-up requirement whenever amount changes
  useEffect(() => {
    const amt = parseFloat(formData.amount);
    if (!amt || amt <= 0) { setStepupInfo(null); return; }
    const controller = new AbortController();
    const check = async () => {
      setStepupChecking(true);
      try {
        const res = await axios.post(
          `${API}/auth/check-stepup`,
          { transaction_type: 'wallet_transfer', amount: amt },
          { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
        );
        setStepupInfo(res.data);
      } catch (_) {
        // ignore abort or network errors during typing
      } finally {
        setStepupChecking(false);
      }
    };
    const t = setTimeout(check, 400); // debounce
    return () => { clearTimeout(t); controller.abort(); };
  }, [formData.amount, token]);

  const requiresStepup = stepupInfo?.required === true;
  const stepupMethod = stepupInfo?.method || 'otp'; // 'otp' | 'biometric'

  const handleRequestOtp = async () => {
    setOtpLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/request-transaction-otp`,
        { transaction_type: 'wallet_transfer', amount: parseFloat(formData.amount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMaskedPhone(res.data.masked_phone || '');
      setOtpStep(true);
      setBiometricStep(false);
      toast.success(res.data.test_otp ? `Test OTP: ${res.data.test_otp}` : `OTP sent to ${res.data.masked_phone}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleBiometricAuth = async () => {
    // Web Authentication API (WebAuthn) — triggers device biometric (fingerprint/Face ID)
    // Falls back to OTP if unavailable or if admin has configured fallback
    if (!window.PublicKeyCredential) {
      if (stepupInfo?.biometric_fallback_otp) {
        toast.info('Biometric not available on this device — sending OTP instead');
        await handleRequestOtp();
      } else {
        toast.error('Biometric authentication is required but not available on this device');
      }
      return;
    }
    try {
      // Minimal WebAuthn get() call — uses any registered platform authenticator
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: crypto.getRandomValues(new Uint8Array(32)),
          timeout: 60000,
          userVerification: 'required',
          rpId: window.location.hostname,
        }
      });
      if (credential) {
        // Biometric succeeded — treat as verified, proceed to transfer
        setBiometricStep(true);
        setOtpStep(false);
        toast.success('Biometric verified — proceeding with transfer');
        // Directly execute transfer without a separate OTP
        await doTransfer(true);
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Biometric authentication was cancelled or failed');
      } else if (stepupInfo?.biometric_fallback_otp) {
        toast.info('Biometric failed — sending OTP instead');
        await handleRequestOtp();
      } else {
        toast.error('Biometric authentication failed');
      }
    }
  };

  const doTransfer = async (skipStepup = false) => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/wallet/transfer`,
        {
          recipient_wallet_id: formData.recipient_wallet_id,
          amount: parseFloat(formData.amount),
          description: formData.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transfer successful!');
      setFormData({ recipient_wallet_id: '', amount: '', description: '' });
      setOtpStep(false);
      setBiometricStep(false);
      setOtp('');
      setStepupInfo(null);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (requiresStepup) {
      if (stepupMethod === 'biometric' && !biometricStep && !otpStep) {
        await handleBiometricAuth();
        return;
      }
      if (stepupMethod === 'otp' && !otpStep) {
        await handleRequestOtp();
        return;
      }
      if (otpStep) {
        if (!otp) { toast.error('Please enter your OTP'); return; }
        setOtpLoading(true);
        try {
          await axios.post(
            `${API}/auth/verify-transaction-otp`,
            { otp, transaction_ref: '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Invalid OTP');
          setOtpLoading(false);
          return;
        } finally {
          setOtpLoading(false);
        }
      }
    }

    await doTransfer();
  };

  const stepupTriggerLabel = stepupInfo?.reason
    ? `Step-up required: ${stepupInfo.reason}`
    : null;

  return (
    <Card data-testid="wallet-transfer-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><Wallet className="inline mr-2" />Wallet to Wallet Transfer</CardTitle>
        <CardDescription>Transfer funds to another Mojo Money wallet</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="recipient_wallet_id">Recipient Wallet ID</Label>
            <Input
              id="recipient_wallet_id"
              data-testid="recipient-wallet-id-input"
              placeholder="MWXXXXXXXXXXXX"
              value={formData.recipient_wallet_id}
              onChange={(e) => setFormData({ ...formData, recipient_wallet_id: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="transfer_amount">Amount (ZAR)</Label>
            <Input
              id="transfer_amount"
              type="number"
              step="0.01"
              data-testid="transfer-amount-input"
              value={formData.amount}
              onChange={(e) => {
                setFormData({ ...formData, amount: e.target.value });
                setOtpStep(false);
                setBiometricStep(false);
                setOtp('');
              }}
              required
            />
            {stepupTriggerLabel && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                {stepupTriggerLabel}
                {stepupMethod === 'biometric' ? ' — biometric verification required' : ' — OTP verification required'}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="transfer_description">Description (Optional)</Label>
            <Input
              id="transfer_description"
              data-testid="transfer-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          {otpStep && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
              <p className="text-sm font-medium text-amber-800">Enter the OTP sent to {maskedPhone}</p>
              <Input
                placeholder="6-digit OTP"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="h-9"
              />
              <button
                type="button"
                onClick={handleRequestOtp}
                className="text-xs text-[#145539] hover:underline"
                disabled={otpLoading}
              >
                Resend OTP
              </button>
            </div>
          )}

          <Button
            type="submit"
            data-testid="submit-transfer-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={loading || otpLoading || stepupChecking}
          >
            {loading || otpLoading
              ? 'Processing...'
              : stepupChecking
              ? 'Checking...'
              : requiresStepup && stepupMethod === 'biometric' && !biometricStep && !otpStep
              ? 'Verify with Biometric'
              : requiresStepup && stepupMethod === 'otp' && !otpStep
              ? 'Send OTP & Continue'
              : otpStep
              ? 'Verify OTP & Send Transfer'
              : 'Send Transfer'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ACE Cross-Border Transfer Component
const ACECrossBorderTransfer = ({ refreshData }) => {
  const [formData, setFormData] = useState({
    recipient_identifier: '',
    recipient_type: 'phone',
    amount: '',
    recipient_currency: 'ZAR',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [limits, setLimits] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [aceInfo, setAceInfo] = useState(null);
  const token = localStorage.getItem('mojo_token');

  useEffect(() => {
    fetchLimits();
    fetchCurrencies();
    fetchACEInfo();
  }, []);

  const fetchLimits = async () => {
    try {
      const response = await axios.get(`${API}/ace-transfer/limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch limits');
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/ace-transfer/currencies`);
      setCurrencies(response.data.currencies);
    } catch (error) {
      console.error('Failed to fetch currencies');
    }
  };

  const fetchACEInfo = async () => {
    try {
      const response = await axios.get(`${API}/ace-token/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAceInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch ACE info');
    }
  };

  const calculateFee = () => {
    if (!formData.amount) return 0;
    const amount = parseFloat(formData.amount);
    const percentageFee = amount * 0.035; // 3.5%
    return Math.min(30, percentageFee);
  };

  const calculateACE = () => {
    if (!formData.amount || !aceInfo) return 0;
    const amount = parseFloat(formData.amount);
    return (amount / aceInfo.current_value_zar).toFixed(6);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ace-transfer/cross-border`,
        {
          recipient_identifier: formData.recipient_identifier,
          recipient_type: formData.recipient_type,
          amount: parseFloat(formData.amount),
          recipient_currency: formData.recipient_currency,
          description: formData.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
      toast.success(response.data.message || 'Transfer successful!');
      
      // Clear form
      setFormData({
        recipient_identifier: '',
        recipient_type: formData.recipient_type,
        amount: '',
        recipient_currency: formData.recipient_currency,
        description: ''
      });
      refreshData();
      fetchLimits(); // Refresh limits after transfer
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const fee = calculateFee();
  const aceAmount = calculateACE();

  return (
    <Card data-testid="ace-cross-border-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]">
          <ArrowRightLeft className="inline mr-2" />Send Money Across Africa with ACE
        </CardTitle>
        <CardDescription>Cross-border transfers using ACE stable coin via Interledger Protocol</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Limits & Info Banner */}
        {limits && (
          <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg">
            <p className="text-sm font-medium text-[#145539] mb-1">
              💰 Monthly Limit: R{limits.monthly_remaining.toFixed(2)} remaining of R{limits.monthly_limit.toFixed(2)}
            </p>
            <p className="text-xs text-[#1B0C34]/70">
              • Fee: Lesser of R30 or 3.5% per transfer<br/>
              • {limits.verification_status ? '✅ ID Verified (R25,000 limit)' : '⚠️ Not Verified (R14,000 limit - Complete ID verification for R25,000)'}<br/>
              • ACE Token Value: R{aceInfo?.current_value_zar || '0'} per token
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Recipient Type */}
          <div>
            <Label>Recipient Identification</Label>
            <div className="grid grid-cols-3 gap-2">
              <Button
                type="button"
                data-testid="select-phone-btn"
                variant={formData.recipient_type === 'phone' ? 'default' : 'outline'}
                className={formData.recipient_type === 'phone' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, recipient_type: 'phone' })}
              >
                Phone
              </Button>
              <Button
                type="button"
                data-testid="select-email-btn"
                variant={formData.recipient_type === 'email' ? 'default' : 'outline'}
                className={formData.recipient_type === 'email' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, recipient_type: 'email' })}
              >
                Email
              </Button>
              <Button
                type="button"
                data-testid="select-wallet-btn"
                variant={formData.recipient_type === 'wallet_id' ? 'default' : 'outline'}
                className={formData.recipient_type === 'wallet_id' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                onClick={() => setFormData({ ...formData, recipient_type: 'wallet_id' })}
              >
                Wallet ID
              </Button>
            </div>
          </div>

          {/* Recipient Identifier */}
          <div>
            <Label htmlFor="recipient_identifier">
              {formData.recipient_type === 'phone' ? 'Recipient Phone Number' :
               formData.recipient_type === 'email' ? 'Recipient Email' : 'Recipient Wallet ID'}
            </Label>
            <Input
              id="recipient_identifier"
              data-testid="recipient-identifier-input"
              placeholder={
                formData.recipient_type === 'phone' ? '+27XXXXXXXXX' :
                formData.recipient_type === 'email' ? 'user@example.com' : 'MWXXXXXXXXXX'
              }
              value={formData.recipient_identifier}
              onChange={(e) => setFormData({ ...formData, recipient_identifier: e.target.value })}
              required
            />
          </div>

          {/* Amount */}
          <div>
            <Label htmlFor="ace_amount">Amount (ZAR)</Label>
            <Input
              id="ace_amount"
              type="number"
              step="0.01"
              data-testid="ace-amount-input"
              placeholder="Enter amount to send"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
            {formData.amount && aceInfo && (
              <div className="mt-2 p-2 bg-[#FAF5F0] rounded text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Send Amount:</span>
                  <span className="font-semibold text-[#1B0C34]">R{parseFloat(formData.amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">ACE Tokens:</span>
                  <span className="font-semibold text-[#145539]">{aceAmount} ACE</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1B0C34]/60">Transfer Fee:</span>
                  <span className="text-red-600">R{fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-[#1B0C34]/10 mt-1">
                  <span className="font-semibold text-[#1B0C34]">Total Deducted:</span>
                  <span className="font-bold text-red-600">R{(parseFloat(formData.amount) + fee).toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Recipient Currency */}
          <div>
            <Label htmlFor="recipient_currency">Recipient Currency</Label>
            <Select
              value={formData.recipient_currency}
              onValueChange={(value) => setFormData({ ...formData, recipient_currency: value })}
            >
              <SelectTrigger data-testid="currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((curr) => (
                  <SelectItem key={curr.code} value={curr.code}>
                    {curr.symbol} {curr.code} - {curr.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#1B0C34]/60 mt-1">
              Recipient will receive ACE tokens (can convert to {formData.recipient_currency})
            </p>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description (Optional)</Label>
            <Input
              id="description"
              data-testid="description-input"
              placeholder="Purpose of transfer"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <Button
            type="submit"
            data-testid="submit-ace-transfer-btn"
            className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing via Interledger...' : 'Send Money via ACE'}
          </Button>
        </form>

        {/* Success Result */}
        {result && (
          <div className="mt-4 p-4 bg-[#145539] text-white rounded-lg" data-testid="ace-transfer-result">
            <p className="font-bold text-lg mb-2">✅ Transfer Successful!</p>
            <div className="space-y-2 text-sm">
              <div className="p-2 bg-white/10 rounded">
                <p className="text-xs opacity-70">Transaction ID</p>
                <p className="font-mono text-xs">{result.ilp_transaction_id}</p>
              </div>
              <div className="flex justify-between">
                <span>Amount Sent:</span>
                <span className="font-bold">R{result.amount_sent?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>ACE Tokens:</span>
                <span className="font-bold text-[#F2B10C]">{result.ace_amount} ACE</span>
              </div>
              <div className="flex justify-between">
                <span>Recipient Gets:</span>
                <span className="font-bold">{result.recipient?.currency} {result.recipient?.amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Transfer Fee:</span>
                <span>R{result.transfer_fee?.toFixed(2)}</span>
              </div>
              <div className="pt-2 border-t border-white/20">
                <p className="text-xs opacity-70">Recipient: {result.recipient?.identifier}</p>
                <p className="text-xs opacity-70">Protocol: {result.protocol}</p>
                <p className="text-xs opacity-70">Monthly Remaining: R{result.limits?.monthly_remaining?.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Money Transfer Component
const MoneyTransfer = ({ refreshData }) => {
  const [formData, setFormData] = useState({ 
    recipient_phone: '', 
    amount: '', 
    transfer_type: 'send',
    voucher_code: '',
    verification_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const token = localStorage.getItem('mojo_token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const requestData = {
        recipient_phone: formData.recipient_phone,
        amount: parseFloat(formData.amount),
        transfer_type: formData.transfer_type
      };

      // Add voucher details if redeeming
      if (formData.transfer_type === 'redeem') {
        requestData.voucher_code = formData.voucher_code;
        requestData.verification_code = formData.verification_code;
      }

      const response = await axios.post(
        `${API}/money-transfer`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
      toast.success(response.data.message || 'Transaction successful!');
      
      // Clear form except transfer type
      setFormData({ 
        ...formData,
        recipient_phone: '', 
        amount: '',
        voucher_code: '',
        verification_code: ''
      });
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="money-transfer-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><ArrowRightLeft className="inline mr-2" />Money Transfer</CardTitle>
        <CardDescription>Send or redeem money transfers using SPAZACASH vouchers.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg">
          <p className="text-sm font-medium text-[#145539] mb-1">💰 Merchant Commission: R2.00 per transaction</p>
          <p className="text-xs text-[#1B0C34]/70">
            • Send: Transfer fee R7 per R3000 + R2 commission<br/>
            • Redeem: R2 commission per redemption<br/>
          </p>
        </div>

        <div>
          <Label>Transfer Type</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              data-testid="select-send-btn"
              variant={formData.transfer_type === 'send' ? 'default' : 'outline'}
              className={formData.transfer_type === 'send' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
              onClick={() => {
                setFormData({ ...formData, transfer_type: 'send' });
                setResult(null);
              }}
            >
              Send
            </Button>
            <Button
              type="button"
              data-testid="select-redeem-btn"
              variant={formData.transfer_type === 'redeem' ? 'default' : 'outline'}
              className={formData.transfer_type === 'redeem' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
              onClick={() => {
                setFormData({ ...formData, transfer_type: 'redeem' });
                setResult(null);
              }}
            >
              Redeem
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {formData.transfer_type === 'send' ? (
            <>
              <div>
                <Label htmlFor="recipient_phone">Recipient Phone Number</Label>
                <Input
                  id="recipient_phone"
                  data-testid="recipient-phone-input"
                  placeholder="+27XXXXXXXXX"
                  value={formData.recipient_phone}
                  onChange={(e) => setFormData({ ...formData, recipient_phone: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="mt_amount">Amount (ZAR)</Label>
                <Input
                  id="mt_amount"
                  type="number"
                  step="0.01"
                  data-testid="money-transfer-amount-input"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
                {formData.amount && (
                  <p className="text-xs text-[#1B0C34]/60 mt-1">
                    Transfer fee: R{((parseFloat(formData.amount) / 3000) * 7).toFixed(2)}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <Label htmlFor="voucher_code">Voucher Code</Label>
                <Input
                  id="voucher_code"
                  data-testid="voucher-code-input"
                  placeholder="Enter 16-digit voucher code"
                  value={formData.voucher_code}
                  onChange={(e) => setFormData({ ...formData, voucher_code: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="verification_code">Verification Code</Label>
                <Input
                  id="verification_code"
                  data-testid="verification-code-input"
                  placeholder="Enter 4-digit code"
                  maxLength="4"
                  value={formData.verification_code}
                  onChange={(e) => setFormData({ ...formData, verification_code: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="redeem_amount">Amount to Redeem (ZAR)</Label>
                <Input
                  id="redeem_amount"
                  type="number"
                  step="0.01"
                  data-testid="redeem-amount-input"
                  placeholder="Full or partial amount"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
                <p className="text-xs text-[#1B0C34]/60 mt-1">
                  Enter the amount you want to redeem (can be partial)
                </p>
              </div>
              <div>
                <Label htmlFor="recipient_phone_redeem">Recipient Phone (Optional)</Label>
                <Input
                  id="recipient_phone_redeem"
                  placeholder="+27XXXXXXXXX"
                  value={formData.recipient_phone}
                  onChange={(e) => setFormData({ ...formData, recipient_phone: e.target.value })}
                />
              </div>
            </>
          )}

          <Button
            type="submit"
            data-testid="submit-money-transfer-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={loading}
          >
            {loading ? 'Processing...' : (formData.transfer_type === 'send' ? 'Send Money' : 'Redeem Voucher')}
          </Button>
        </form>

        {result && formData.transfer_type === 'send' && (result.voucher_code || result.transfer_code) && (
          <div className="mt-4 p-4 bg-[#145539] text-white rounded-lg" data-testid="transfer-code-result">
            <p className="font-bold text-lg mb-2">✅ Voucher Created!</p>
            <div className="space-y-2 text-sm">
              <div className="p-2 bg-white/10 rounded">
                <p className="text-xs opacity-70">Voucher Code</p>
                <p className="font-mono font-bold text-base">{result.voucher_code || result.transfer_code}</p>
              </div>
              <div className="p-2 bg-white/10 rounded">
                <p className="text-xs opacity-70">Verification Code</p>
                <p className="font-mono font-bold text-base">{result.verification_code}</p>
              </div>
              {result.expiry_date && (
                <div className="p-2 bg-white/10 rounded">
                  <p className="text-xs opacity-70">Expires</p>
                  <p className="text-sm">{new Date(result.expiry_date).toLocaleString()}</p>
                </div>
              )}
              <div className="pt-2 border-t border-white/20">
                <p className="text-xs opacity-70">Amount</p>
                <p className="font-semibold">R{result.amount?.toFixed(2)}</p>
              </div>
              <div className="text-xs opacity-80 mt-3 p-2 bg-[#F2B10C]/20 rounded">
                📱 Share both codes with {result.recipient_phone} to redeem
              </div>
            </div>
          </div>
        )}

        {result && formData.transfer_type === 'redeem' && (
          <div className="mt-4 p-4 bg-[#145539] text-white rounded-lg" data-testid="redeem-result">
            <p className="font-bold text-lg mb-2">✅ Voucher Redeemed!</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Amount Redeemed:</span>
                <span className="font-bold">R{result.amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Commission:</span>
                <span className="font-bold text-[#F2B10C]">+R{result.merchant_commission?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-white/20">
                <span>Total Credited:</span>
                <span className="font-bold text-lg">R{result.total_credited?.toFixed(2)}</span>
              </div>
              {result.remaining_balance !== undefined && result.remaining_balance > 0 && (
                <div className="mt-2 p-2 bg-white/10 rounded">
                  <p className="text-xs opacity-70">Voucher Remaining Balance</p>
                  <p className="font-semibold">R{result.remaining_balance.toFixed(2)}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ACE Token Component
const ACEToken = ({ wallet, refreshData }) => {
  const [formData, setFormData] = useState({ amount: '', operation: 'buy' });
  const [loading, setLoading] = useState(false);
  const [aceInfo, setAceInfo] = useState(null);
  const token = localStorage.getItem('mojo_token');

  useEffect(() => {
    fetchACEInfo();
  }, []);

  const fetchACEInfo = async () => {
    try {
      const response = await axios.get(`${API}/ace-token/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAceInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch ACE info');
    }
  };

  const calculateTokens = () => {
    if (!formData.amount || !aceInfo) return 0;
    if (formData.operation === 'buy') {
      return (parseFloat(formData.amount) / aceInfo.current_value_zar).toFixed(4);
    }
    return (parseFloat(formData.amount) * aceInfo.current_value_zar).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ace-token`,
        {
          amount: parseFloat(formData.amount),
          operation: formData.operation
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const tokensAmount = formData.operation === 'buy' ? response.data.ace_tokens : response.data.zar_received;
      toast.success(`Success! ${formData.operation === 'buy' ? `Received ${tokensAmount} ACE` : `Received R${tokensAmount}`}`);
      setFormData({ amount: '', operation: formData.operation });
      refreshData();
      fetchACEInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="ace-token-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><Coins className="inline mr-2" />ACE Token Trading</CardTitle>
        <CardDescription>Carbon tax-backed tokens on Hedera DLT (2.5 year minting cycles)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {aceInfo && (
          <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="text-sm opacity-80">Current ACE Token Value</p>
                <p className="text-3xl font-bold">R {aceInfo.current_value_zar}</p>
                <p className="text-xs opacity-70 mt-1">Based on SA Carbon Tax (R{aceInfo.carbon_tax_rate}/tonne - 25%)</p>
              </div>
              <div className="text-right">
                <p className="text-xs opacity-80">Cycle {aceInfo.minting_cycle_years}yr</p>
                <p className="text-sm font-semibold">{aceInfo.days_remaining_in_cycle} days left</p>
              </div>
            </div>
            <div className="pt-3 border-t border-white/20">
              <p className="text-xs opacity-80">💹 Value appreciates with annual carbon tax increases</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 p-4 bg-[#FAF5F0] rounded-lg">
          <div>
            <p className="text-sm text-[#1B0C34]/60">Wallet Balance</p>
            <p className="text-xl font-bold text-[#1B0C34]">R {wallet?.balance?.toFixed(2) || '0.00'}</p>
          </div>
          <div>
            <p className="text-sm text-[#1B0C34]/60">ACE Token Balance</p>
            <p className="text-xl font-bold text-[#F2B10C]">{wallet?.ace_token_balance?.toFixed(4) || '0.0000'}</p>
          </div>
        </div>

        <div>
          <Label>Operation</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              data-testid="select-buy-btn"
              variant={formData.operation === 'buy' ? 'default' : 'outline'}
              className={formData.operation === 'buy' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
              onClick={() => setFormData({ ...formData, operation: 'buy' })}
            >
              Buy ACE
            </Button>
            <Button
              type="button"
              data-testid="select-redeem-ace-btn"
              variant={formData.operation === 'redeem' ? 'default' : 'outline'}
              className={formData.operation === 'redeem' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
              onClick={() => setFormData({ ...formData, operation: 'redeem' })}
            >
              Redeem ACE
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="ace_amount">Amount ({formData.operation === 'buy' ? 'ZAR to Spend' : 'ACE Tokens'})</Label>
            <Input
              id="ace_amount"
              type="number"
              step="0.01"
              data-testid="ace-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
            {formData.amount && aceInfo && (
              <p className="text-sm text-[#145539] font-medium mt-2">
                ≈ {formData.operation === 'buy' 
                  ? `${calculateTokens()} ACE tokens` 
                  : `R${calculateTokens()} ZAR`}
              </p>
            )}
            {aceInfo && (
              <p className="text-xs text-[#1B0C34]/60 mt-1">
                1 ACE = R{aceInfo.current_value_zar} ZAR (Carbon-backed)
              </p>
            )}
          </div>
          <Button
            type="submit"
            data-testid="submit-ace-token-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing on Hedera DLT...' : (formData.operation === 'buy' ? 'Buy ACE Tokens' : 'Redeem ACE Tokens')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Normalise a SA mobile number to MSISDN form (27XXXXXXXXX).
const normalizeMsisdn = (raw) => {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.startsWith('27')) return d;
  if (d.startsWith('0')) return '27' + d.slice(1);
  if (d.length === 9) return '27' + d;
  return d;
};

// Airtime & Data — now backed by the real Mojo Air (Limes) catalog. Picks a
// priced product from the live catalogue and places a real order that charges
// the wallet; the old /airtime-data path fabricated a voucher and delivered
// nothing (audit A7), so it has been retired here.
const AirtimeData = ({ refreshData }) => {
  const token = localStorage.getItem('mojo_token');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [form, setForm] = useState({ msisdn: '', sku: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoadingProducts(true);
    axios
      .get(`${API}/limes/products?page=1&limit=100`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!alive) return;
        const raw = r.data?.data || r.data?.items || [];
        // The order endpoint accepts the numeric sku only, and we only sell
        // fixed-price products here (a real, chargeable amount). Sort by name.
        const items = (Array.isArray(raw) ? raw : [])
          .filter((p) => p && p.sku && /^\d+$/.test(String(p.sku)) && p.name && Number(p.price) > 0)
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setProducts(items);
      })
      .catch((e) => {
        const d = e?.response?.data?.detail || e?.message;
        toast.error(`Failed to load catalogue: ${typeof d === 'string' ? d : 'Unknown error'}`);
      })
      .finally(() => alive && setLoadingProducts(false));
    return () => { alive = false; };
  }, [token]);

  const chosen = products.find((p) => String(p.sku) === String(form.sku));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msisdn = normalizeMsisdn(form.msisdn);
    if (!/^27\d{9}$/.test(msisdn)) return toast.error('Enter a valid SA number, e.g. 0821234567');
    if (!form.sku) return toast.error('Please pick a product');
    setLoading(true);
    try {
      await axios.post(
        `${API}/limes/order`,
        { msisdn, products: [{ id: form.sku }] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${chosen?.name || 'Product'} sent to ${msisdn}`);
      setForm({ msisdn: '', sku: '' });
      refreshData?.();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Purchase failed';
      const s = typeof detail === 'string' ? detail : JSON.stringify(detail);
      if (s.toLowerCase().includes('subscriber does not exist')) {
        toast.error('That number is not an active Mojo Air subscriber. Activate a SIM first.');
      } else {
        toast.error(s);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="airtime-data-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]"><Smartphone className="inline mr-2" />Airtime & Data</CardTitle>
        <CardDescription>Buy airtime or a data bundle from the Mojo Air catalogue. Charged to your wallet.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="airtime-product">Product</Label>
            <Select value={form.sku} onValueChange={(v) => setForm({ ...form, sku: v })} disabled={loadingProducts}>
              <SelectTrigger data-testid="airtime-product-select">
                <SelectValue placeholder={loadingProducts ? 'Loading catalogue…' : 'Pick a product'} />
              </SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={p.sku} value={String(p.sku)}>
                    {p.name} — R{Number(p.price).toFixed(2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="airtime-phone">Recipient number</Label>
            <Input
              id="airtime-phone"
              data-testid="airtime-phone-input"
              placeholder="0821234567"
              value={form.msisdn}
              onChange={(e) => setForm({ ...form, msisdn: e.target.value })}
              required
            />
          </div>
          {chosen && (
            <p className="text-sm text-gray-600" data-testid="airtime-cost-preview">
              Cost: <span className="font-semibold text-[#1B0C34]">R{Number(chosen.price).toFixed(2)}</span> — charged to your wallet.
            </p>
          )}
          <Button
            type="submit"
            data-testid="submit-airtime-data-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={loading || loadingProducts || !form.sku}
          >
            {loading ? 'Processing…' : chosen ? `Buy for R${Number(chosen.price).toFixed(2)}` : 'Buy'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Customer ID Verification Component
const CustomerIDVerification = ({ refreshData }) => {
  const [step, setStep] = useState('details'); // 'details', 'verify', 'result'
  const [formData, setFormData] = useState({
    customer_name: '',
    id_type: 'id_card',
    id_number: '',
    phone_number: ''
  });
  const [sessionData, setSessionData] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleInitiate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/customer-verification/initiate`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSessionData(response.data);
      setStep('verify');
      toast.success('Verification session initiated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate verification');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenVerification = () => {
    if (!sessionData?.verification_url) return;

    // Open VirtuallVerify in popup
    const width = 800;
    const height = 900;
    const left = (window.screen.width / 2) - (width / 2);
    const top = (window.screen.height / 2) - (height / 2);
    
    const verifyWindow = window.open(
      sessionData.verification_url,
      'CustomerVerification',
      `width=${width},height=${height},top=${top},left=${left}`
    );

    toast.info('Customer should complete face capture and ID verification');

    // Poll for window close
    const pollTimer = setInterval(() => {
      if (verifyWindow && verifyWindow.closed) {
        clearInterval(pollTimer);
        toast.info('Verification window closed. Click "Complete Verification" when ready.');
      }
    }, 1000);
  };

  const handleComplete = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/customer-verification/complete`,
        {
          customer_name: formData.customer_name,
          id_number: formData.id_number,
          verification_token: sessionData.access_token,
          delivery_method: deliveryMethod,
          delivery_address: deliveryAddress
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.verified) {
        setVerificationResult(response.data);
        setStep('result');
        toast.success(`Verification successful! Commission: R${response.data.commission_earned}`);
        refreshData();
      } else {
        toast.error(response.data.message || 'Verification failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification completion failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewVerification = () => {
    setStep('details');
    setFormData({ customer_name: '', id_type: 'id_card', id_number: '', phone_number: '' });
    setSessionData(null);
    setDeliveryAddress('');
    setVerificationResult(null);
  };

  return (
    <Card data-testid="customer-verification-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]">
          <UserCheck className="inline mr-2" />Customer ID Verification
        </CardTitle>
        <CardDescription>Verify customer identity with face capture and ID matching</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#145539] mb-1">💰 Merchant Commission: R1.25 per verification</p>
          <p className="text-xs text-[#1B0C34]/70">
            ✓ Face capture with liveness detection<br/>
            ✓ ID/Passport document validation<br/>
            ✓ 80% minimum match threshold
          </p>
        </div>

        {step === 'details' && (
          <form onSubmit={handleInitiate} className="space-y-4">
            <div>
              <Label htmlFor="customer_name">Customer Full Name</Label>
              <Input
                id="customer_name"
                data-testid="customer-name-input"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>ID Type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  data-testid="select-id-card-btn"
                  variant={formData.id_type === 'id_card' ? 'default' : 'outline'}
                  className={formData.id_type === 'id_card' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                  onClick={() => setFormData({ ...formData, id_type: 'id_card' })}
                >
                  SA ID Card
                </Button>
                <Button
                  type="button"
                  data-testid="select-passport-btn"
                  variant={formData.id_type === 'passport' ? 'default' : 'outline'}
                  className={formData.id_type === 'passport' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                  onClick={() => setFormData({ ...formData, id_type: 'passport' })}
                >
                  Passport
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="id_number">
                {formData.id_type === 'id_card' ? 'ID Number' : 'Passport Number'}
              </Label>
              <Input
                id="id_number"
                data-testid="customer-id-number-input"
                placeholder={formData.id_type === 'id_card' ? '13 digits' : 'Passport number'}
                value={formData.id_number}
                onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="phone_number">Customer Phone Number</Label>
              <Input
                id="phone_number"
                data-testid="customer-phone-input"
                placeholder="+27XXXXXXXXX"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="initiate-verification-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              disabled={loading}
            >
              {loading ? 'Initiating...' : 'Initiate Verification'}
            </Button>
          </form>
        )}

        {step === 'verify' && sessionData && (
          <div className="space-y-4">
            <div className="p-4 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
              <h3 className="font-semibold text-[#1B0C34] mb-2">Verification Session Active</h3>
              <p className="text-sm text-[#1B0C34]/70 mb-3">
                Customer: <strong>{formData.customer_name}</strong><br/>
                ID: <strong>{formData.id_number}</strong>
              </p>
              <Button
                onClick={handleOpenVerification}
                data-testid="open-verification-btn"
                className="w-full bg-[#1B0C34] hover:bg-[#2D1854] text-white mb-2"
              >
                Open VirtuallVerify Portal
              </Button>
              <p className="text-xs text-[#1B0C34]/60 text-center">
                Customer will complete:<br/>
                1. Face capture with liveness detection<br/>
                2. ID/Passport document scan<br/>
                3. Face-to-ID matching
              </p>
            </div>

            <form onSubmit={handleComplete} className="space-y-4">
              <div>
                <Label>Certificate Delivery Method</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    data-testid="select-wallet-cert-btn"
                    variant={deliveryMethod === 'wallet' ? 'default' : 'outline'}
                    className={deliveryMethod === 'wallet' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                    onClick={() => setDeliveryMethod('wallet')}
                  >
                    Wallet
                  </Button>
                  <Button
                    type="button"
                    data-testid="select-email-cert-btn"
                    variant={deliveryMethod === 'email' ? 'default' : 'outline'}
                    className={deliveryMethod === 'email' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                    onClick={() => setDeliveryMethod('email')}
                  >
                    Email
                  </Button>
                  <Button
                    type="button"
                    data-testid="select-whatsapp-cert-btn"
                    variant={deliveryMethod === 'whatsapp' ? 'default' : 'outline'}
                    className={deliveryMethod === 'whatsapp' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                    onClick={() => setDeliveryMethod('whatsapp')}
                  >
                    WhatsApp
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="cert_delivery_address">
                  {deliveryMethod === 'wallet' ? 'Customer Wallet ID' : 
                   deliveryMethod === 'email' ? 'Customer Email' : 'Customer WhatsApp'}
                </Label>
                <Input
                  id="cert_delivery_address"
                  data-testid="cert-delivery-address-input"
                  placeholder={
                    deliveryMethod === 'wallet' ? 'MWXXXXXXXXXXXX' :
                    deliveryMethod === 'email' ? 'customer@email.com' :
                    '+27XXXXXXXXX'
                  }
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="complete-verification-btn"
                className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
                disabled={loading}
              >
                {loading ? 'Completing...' : 'Complete Verification'}
              </Button>
            </form>
          </div>
        )}

        {step === 'result' && verificationResult && (
          <div className="space-y-4" data-testid="verification-result">
            <div className="p-6 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg">
              <div className="text-center mb-4">
                <CheckCircle className="w-20 h-20 mx-auto mb-3" />
                <h3 className="text-3xl font-bold">ID VERIFIED ✓</h3>
              </div>

              <div className="space-y-3 text-sm">
                <div className="p-3 bg-white/10 rounded">
                  <p className="text-xs opacity-70 mb-1">Certificate Number</p>
                  <p className="font-mono font-bold text-lg">{verificationResult.certificate.certificate_number}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white/10 rounded">
                    <p className="text-xs opacity-70">Customer</p>
                    <p className="font-semibold">{verificationResult.certificate.customer_name}</p>
                  </div>
                  <div className="p-3 bg-white/10 rounded">
                    <p className="text-xs opacity-70">ID Number</p>
                    <p className="font-mono text-sm">{verificationResult.certificate.id_number}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="p-2 bg-white/10 rounded text-center">
                    <p className="text-xs opacity-70">Overall</p>
                    <p className="font-bold text-[#F2B10C]">{verificationResult.verification_score.toFixed(1)}%</p>
                  </div>
                  <div className="p-2 bg-white/10 rounded text-center">
                    <p className="text-xs opacity-70">Face Match</p>
                    <p className="font-bold">{verificationResult.face_match_score.toFixed(1)}%</p>
                  </div>
                  <div className="p-2 bg-white/10 rounded text-center">
                    <p className="text-xs opacity-70">ID Valid</p>
                    <p className="font-bold">{verificationResult.id_validation_score.toFixed(1)}%</p>
                  </div>
                </div>

                <div className="pt-3 border-t border-white/20">
                  <div className="flex justify-between items-center">
                    <span>Your Commission:</span>
                    <span className="font-bold text-[#F2B10C] text-xl">R{verificationResult.commission_earned.toFixed(2)}</span>
                  </div>
                </div>

                <div className="p-3 bg-white/10 rounded">
                  <p className="text-xs font-semibold mb-1">Certificate Delivered</p>
                  <p className="text-xs">{verificationResult.delivery_status}</p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleNewVerification}
              data-testid="new-verification-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            >
              Verify Another Customer
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Insurance Premium Collection Component
const InsurancePremium = ({ refreshData, walletBalance = 0 }) => {
  const [step, setStep] = useState('lookup'); // 'lookup', 'collect', 'payment', 'receipt'
  const [lookupType, setLookupType] = useState('id_number');
  const [lookupValue, setLookupValue] = useState('');
  const [policyData, setPolicyData] = useState(null);
  const [premiumAmount, setPremiumAmount] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [receiptData, setReceiptData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cardData, setCardData] = useState({ card_number: '', expiry_date: '', cvv: '', card_type: 'debit' });
  const [cardPaymentTxnId, setCardPaymentTxnId] = useState(null);
  const [processingCard, setProcessingCard] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleLookup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/insurance/lookup?lookup_type=${lookupType}&lookup_value=${lookupValue}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPolicyData(response.data);
      setPremiumAmount(response.data.premium_due.toString());
      setStep('collect');
      toast.success('Policy found!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Policy not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCardPayment = async () => {
    if (!cardData.card_number || !cardData.expiry_date || !cardData.cvv) {
      toast.error('Please enter all card details');
      return;
    }
    setProcessingCard(true);
    try {
      const response = await axios.post(
        `${API}/payment/card`,
        {
          amount: parseFloat(premiumAmount),
          card_number: cardData.card_number.replace(/\s/g, ''),
          expiry_date: cardData.expiry_date,
          cvv: cardData.cvv,
          card_type: cardData.card_type,
          description: `Insurance premium - ${policyData?.policy_holder_name || lookupValue}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCardPaymentTxnId(response.data.transaction_id);
      toast.success(`Card payment approved — R${parseFloat(premiumAmount).toFixed(2)} collected`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Card payment failed');
    } finally {
      setProcessingCard(false);
    }
  };

  const handleCollect = async (e) => {
    e.preventDefault();

    // Card: must process card payment first
    if (paymentMethod === 'card' && !cardPaymentTxnId) {
      toast.error('Please process the card payment before collecting');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/insurance/collect-premium`,
        {
          lookup_type: lookupType,
          lookup_value: lookupValue,
          premium_amount: parseFloat(premiumAmount),
          policy_holder_name: policyData.policy_holder_name,
          receipt_delivery: deliveryMethod,
          delivery_address: deliveryAddress,
          payment_method: paymentMethod,
          card_payment_transaction_id: cardPaymentTxnId || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setReceiptData(response.data);
      setStep('receipt');
      toast.success(`Premium collected! Commission: R${response.data.commission}`);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Collection failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewTransaction = () => {
    setStep('lookup');
    setLookupValue('');
    setPolicyData(null);
    setPremiumAmount('');
    setDeliveryAddress('');
    setReceiptData(null);
    setPaymentMethod('cash');
    setCardData({ card_number: '', expiry_date: '', cvv: '', card_type: 'debit' });
    setCardPaymentTxnId(null);
  };

  return (
    <Card data-testid="insurance-premium-form">
      <CardHeader>
        <CardTitle className="text-[#1B0C34]">
          <Shield className="inline mr-2" />Insurance Premium Collection
        </CardTitle>
        <CardDescription>Collect insurance premiums and earn R1.25 per transaction</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 bg-[#145539]/10 border border-[#145539]/30 rounded-lg mb-4">
          <p className="text-sm font-medium text-[#145539] mb-1">💰 Merchant Commission: R1.25 per transaction</p>
          <p className="text-xs text-[#1B0C34]/70">
            Receipts can be sent via wallet, email, or WhatsApp
          </p>
        </div>

        {step === 'lookup' && (
          <form onSubmit={handleLookup} className="space-y-4">
            <div>
              <Label>Lookup Type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  data-testid="select-id-lookup-btn"
                  variant={lookupType === 'id_number' ? 'default' : 'outline'}
                  className={lookupType === 'id_number' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                  onClick={() => setLookupType('id_number')}
                >
                  ID Number
                </Button>
                <Button
                  type="button"
                  data-testid="select-policy-lookup-btn"
                  variant={lookupType === 'policy_number' ? 'default' : 'outline'}
                  className={lookupType === 'policy_number' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                  onClick={() => setLookupType('policy_number')}
                >
                  Policy Number
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="lookup_value">
                {lookupType === 'id_number' ? 'ID Number' : 'Policy Number'}
              </Label>
              <Input
                id="lookup_value"
                data-testid="lookup-value-input"
                placeholder={lookupType === 'id_number' ? '13 digits' : 'Enter policy number'}
                value={lookupValue}
                onChange={(e) => setLookupValue(e.target.value)}
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="lookup-policy-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              disabled={loading}
            >
              {loading ? 'Looking up...' : 'Lookup Policy'}
            </Button>
          </form>
        )}

        {step === 'collect' && policyData && (
          <div className="space-y-4">
            <div className="p-4 bg-[#FAF5F0] rounded-lg">
              <h3 className="font-semibold text-[#1B0C34] mb-2">Policy Information</h3>
              <div className="space-y-1 text-sm">
                <p><strong>Policy Holder:</strong> {policyData.policy_holder_name}</p>
                <p><strong>Policy Number:</strong> {policyData.policy_number}</p>
                <p><strong>Policy Type:</strong> {policyData.policy_type}</p>
                <p><strong>Provider:</strong> {policyData.insurance_provider}</p>
                <p><strong>Premium Due:</strong> R{policyData.premium_due.toFixed(2)}</p>
                <p><strong>Due Date:</strong> {policyData.due_date}</p>
              </div>
            </div>

            <form onSubmit={handleCollect} className="space-y-4">
              <div>
                <Label htmlFor="premium_amount">Premium Amount (ZAR)</Label>
                <Input
                  id="premium_amount"
                  type="number"
                  step="0.01"
                  data-testid="premium-amount-input"
                  value={premiumAmount}
                  onChange={(e) => setPremiumAmount(e.target.value)}
                  required
                />
              </div>

              {/* Payment Method Selection */}
              <div>
                <Label>Payment Method</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { value: 'cash', label: 'Cash' },
                    { value: 'card', label: 'Card' },
                    { value: 'wallet_qr', label: 'Wallet QR' },
                  ].map(({ value, label }) => (
                    <Button
                      key={value}
                      type="button"
                      variant={paymentMethod === value ? 'default' : 'outline'}
                      className={paymentMethod === value ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                      onClick={() => { setPaymentMethod(value); setCardPaymentTxnId(null); }}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Cash: wallet balance check */}
              {paymentMethod === 'cash' && (() => {
                const premium = parseFloat(premiumAmount) || 0;
                const sufficient = walletBalance >= premium;
                return (
                  <div className={`rounded-lg p-3 text-sm border ${sufficient ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-red-50 border-red-300 text-red-800'}`}>
                    <div className="flex justify-between mb-1">
                      <span>Your wallet balance:</span>
                      <span className="font-semibold">R{Number(walletBalance).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Premium required:</span>
                      <span className="font-semibold">R{premium.toFixed(2)}</span>
                    </div>
                    {!sufficient && premium > 0 && (
                      <p className="mt-2 font-semibold">Insufficient balance — please top up your wallet first.</p>
                    )}
                  </div>
                );
              })()}

              {/* Card: capture and charge */}
              {paymentMethod === 'card' && (
                <div>
                  {cardPaymentTxnId ? (
                    <div className="bg-green-50 border border-green-300 rounded-lg p-3 text-sm text-green-800 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 shrink-0" />
                      <span>Card payment approved. Ref: <strong>{cardPaymentTxnId}</strong></span>
                    </div>
                  ) : (
                    <div className="border rounded-lg p-3 space-y-3 bg-gray-50">
                      <p className="text-sm font-medium text-gray-700">Enter customer card details</p>
                      <Input
                        placeholder="Card number"
                        maxLength={19}
                        value={cardData.card_number}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 16);
                          setCardData({ ...cardData, card_number: val.replace(/(.{4})/g, '$1 ').trim() });
                        }}
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          placeholder="MM/YY"
                          maxLength={5}
                          value={cardData.expiry_date}
                          onChange={(e) => {
                            let val = e.target.value.replace(/\D/g, '').slice(0, 4);
                            if (val.length >= 3) val = val.slice(0, 2) + '/' + val.slice(2);
                            setCardData({ ...cardData, expiry_date: val });
                          }}
                        />
                        <Input
                          placeholder="CVV"
                          maxLength={4}
                          type="password"
                          value={cardData.cvv}
                          onChange={(e) => setCardData({ ...cardData, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                        />
                        <select
                          className="border rounded px-2 text-sm"
                          value={cardData.card_type}
                          onChange={(e) => setCardData({ ...cardData, card_type: e.target.value })}
                        >
                          <option value="debit">Debit (2%)</option>
                          <option value="credit">Credit (3.5%)</option>
                        </select>
                      </div>
                      <Button
                        type="button"
                        onClick={handleCardPayment}
                        disabled={processingCard || !cardData.card_number || !cardData.expiry_date || !cardData.cvv || !premiumAmount}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {processingCard ? 'Processing...' : `Charge R${parseFloat(premiumAmount || 0).toFixed(2)} to Card`}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Wallet QR instruction */}
              {paymentMethod === 'wallet_qr' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                  Have the customer scan your wallet QR code to pay <strong>R{parseFloat(premiumAmount || 0).toFixed(2)}</strong> and wait for confirmation before proceeding.
                </div>
              )}

              <div>
                <Label>Receipt Delivery Method</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    data-testid="select-wallet-delivery-btn"
                    variant={deliveryMethod === 'wallet' ? 'default' : 'outline'}
                    className={deliveryMethod === 'wallet' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                    onClick={() => setDeliveryMethod('wallet')}
                  >
                    Wallet
                  </Button>
                  <Button
                    type="button"
                    data-testid="select-email-delivery-btn"
                    variant={deliveryMethod === 'email' ? 'default' : 'outline'}
                    className={deliveryMethod === 'email' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                    onClick={() => setDeliveryMethod('email')}
                  >
                    Email
                  </Button>
                  <Button
                    type="button"
                    data-testid="select-whatsapp-delivery-btn"
                    variant={deliveryMethod === 'whatsapp' ? 'default' : 'outline'}
                    className={deliveryMethod === 'whatsapp' ? 'bg-[#F2B10C] text-[#1B0C34]' : ''}
                    onClick={() => setDeliveryMethod('whatsapp')}
                  >
                    WhatsApp
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="delivery_address">
                  {deliveryMethod === 'wallet' ? 'Wallet ID' : deliveryMethod === 'email' ? 'Email Address' : 'Phone Number'}
                </Label>
                <Input
                  id="delivery_address"
                  data-testid="delivery-address-input"
                  placeholder={
                    deliveryMethod === 'wallet' ? 'MWXXXXXXXXXXXX' :
                    deliveryMethod === 'email' ? 'email@example.com' :
                    '+27XXXXXXXXX'
                  }
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  required
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep('lookup')}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  data-testid="collect-premium-btn"
                  className="flex-1 bg-[#145539] hover:bg-[#1e7049] text-white font-semibold disabled:opacity-50"
                  disabled={
                    loading ||
                    (paymentMethod === 'card' && !cardPaymentTxnId) ||
                    (paymentMethod === 'cash' && walletBalance < (parseFloat(premiumAmount) || 0))
                  }
                >
                  {loading ? 'Processing...' : paymentMethod === 'card' && !cardPaymentTxnId ? 'Process Card First' : 'Collect Premium'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {step === 'receipt' && receiptData && (
          <div className="space-y-4" data-testid="receipt-view">
            <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg">
              <div className="text-center mb-4">
                <CheckCircle className="w-16 h-16 mx-auto mb-2" />
                <h3 className="text-2xl font-bold">Payment Successful!</h3>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Receipt Number:</span>
                  <span className="font-mono font-bold">{receiptData.receipt_number}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Transaction ID:</span>
                  <span className="font-mono text-xs">{receiptData.transaction_id}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Premium Amount:</span>
                  <span className="font-bold">R{receiptData.premium_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/20">
                  <span>Your Commission:</span>
                  <span className="font-bold text-[#F2B10C]">R{receiptData.commission.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span>Total Credited:</span>
                  <span className="font-bold text-[#F2B10C]">R{receiptData.total_credited.toFixed(2)}</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-white/10 rounded">
                <p className="text-xs font-semibold mb-1">Receipt Delivery</p>
                <p className="text-sm">{receiptData.delivery_status}</p>
              </div>
            </div>

            <Button
              onClick={handleNewTransaction}
              data-testid="new-transaction-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            >
              New Transaction
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Mojo Air Reseller — merchant sells physical SIMs (in-hand) and tops up
// existing Mojo Air MSISDNs. Powered by /api/limes/* endpoints.
// ============================================================================
const MojoAirReseller = ({ refreshData }) => {
  const token = localStorage.getItem('mojo_token');
  const [mode, setMode] = useState('sim'); // 'sim' | 'topup'
  const [earningsBump, setEarningsBump] = useState(0); // bump to refetch panel after a sale

  const triggerEarningsRefresh = () => setEarningsBump((b) => b + 1);
  const onSaleSuccess = () => { triggerEarningsRefresh(); refreshData?.(); };

  return (
    <div className="space-y-4">
      <MojoAirEarningsPanel token={token} bump={earningsBump} />

      <Card data-testid="mojo-air-reseller">
        <CardHeader>
          <CardTitle className="text-[#1B0C34] flex items-center gap-2">
            <SignalHigh className="text-emerald-600" /> Sell Mojo Air
          </CardTitle>
          <CardDescription>
            Activate physical Mojo Air SIMs (SIM in Hand) and top up airtime / data on
            existing Mojo Air numbers. Powered by the Mojo Air (Limes) MVNO.
          </CardDescription>
        </CardHeader>
        <CardContent>
        <div className="flex gap-2 mb-4">
          <Button
            type="button"
            data-testid="select-mode-sim"
            variant={mode === 'sim' ? 'default' : 'outline'}
            className={mode === 'sim' ? 'bg-emerald-600 hover:bg-emerald-700 text-white flex-1' : 'flex-1'}
            onClick={() => setMode('sim')}
          >
            Activate New SIM
          </Button>
          <Button
            type="button"
            data-testid="select-mode-topup"
            variant={mode === 'topup' ? 'default' : 'outline'}
            className={mode === 'topup' ? 'bg-emerald-600 hover:bg-emerald-700 text-white flex-1' : 'flex-1'}
            onClick={() => setMode('topup')}
          >
            Top-up MSISDN
          </Button>
        </div>

          {mode === 'sim'
            ? <MojoAirNewSim token={token} refreshData={onSaleSuccess} />
            : <MojoAirTopUp  token={token} refreshData={onSaleSuccess} />}
        </CardContent>
      </Card>
    </div>
  );
};

// Compact earnings panel above the reseller card. Re-fetches when `bump`
// changes (i.e. after every successful activation or top-up).
const MojoAirEarningsPanel = ({ token, bump }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/limes/earnings/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { /* panel is informational; silent on failure */ })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token, bump]);

  const fmt = (n) => `R ${(parseFloat(n || 0)).toFixed(2)}`;
  const lifetime = data?.lifetime_by_type || {};
  const monthly  = data?.this_month_by_type || {};

  // Pretty labels for the txn types
  const labels = {
    MOJO_AIR_ACTIVATION_COMMISSION: 'SIM activations',
    MOJO_AIR_TOPUP_COMMISSION:      'Top-up sales',
    MOJO_AIR_LTV_COMMISSION:        'Ongoing (LTV)',
  };

  return (
    <Card data-testid="mojo-air-earnings-panel" className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white print:hidden">
      <CardHeader className="pb-2">
        <CardTitle className="text-[#1B0C34] text-base flex items-center gap-2">
          <SignalHigh size={18} className="text-emerald-600" /> Your Mojo Air earnings
        </CardTitle>
        <CardDescription>Commission credits already in your wallet.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading && !data ? (
          <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14} /> Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <Stat label="Today"      value={fmt(data?.today_total)} />
              <Stat label="This week"  value={fmt(data?.week_total)} />
              <Stat label="This month" value={fmt(data?.month_total)} />
            </div>
            {Object.keys(monthly).length > 0 && (
              <div className="border-t border-emerald-100 pt-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">This month by type</p>
                <ul className="text-sm text-[#1B0C34] space-y-0.5">
                  {Object.entries(monthly).map(([k, v]) => (
                    <li key={k} className="flex justify-between">
                      <span>{labels[k] || k} <span className="text-xs text-gray-400">× {v.count}</span></span>
                      <span className="font-mono">{fmt(v.total)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {Object.keys(lifetime).length === 0 && (
              <p className="text-xs text-gray-500 italic">
                No Mojo Air sales yet. Activate your first SIM below to start earning.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-white border border-emerald-100 rounded p-2 text-center">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-lg font-bold text-emerald-700 font-mono">{value}</p>
  </div>
);

// ── Activate New SIM (SIM-in-Hand flow) ────────────────────────────────────
const MojoAirNewSim = ({ token, refreshData }) => {
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [form, setForm] = useState({
    productId: '',
    iccid: '',
    customerName: '',
    customerIdNumber: '',
    customerMsisdnContact: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoadingPackages(true);
    axios
      .get(`${API}/limes/subscription-products?category=gsm_packages_sa&page=1&limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (!alive) return;
        const data = r.data?.data || [];
        setPackages(data);
        if (data.length > 0) setForm((f) => ({ ...f, productId: data[0].id }));
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail || e?.message;
        toast.error(`Failed to load Mojo Air SIM packages: ${typeof detail === 'string' ? detail : 'Unknown error'}`);
      })
      .finally(() => alive && setLoadingPackages(false));
    return () => { alive = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.productId) return toast.error('Please pick a SIM package');
    const iccid = form.iccid.replace(/\s+/g, '');
    if (!/^\d{18,22}$/.test(iccid)) return toast.error('ICCID should be 18–22 digits (check the SIM card barcode)');
    if (!form.customerName.trim()) return toast.error('Please enter the customer name');
    if (!/^\d{13}$/.test(form.customerIdNumber.trim())) return toast.error('SA ID number must be 13 digits');

    setSubmitting(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/limes/subscriptions`,
        { productId: form.productId, iccid, eSim: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(r.data);
      toast.success('Mojo Air SIM activated');
      refreshData?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Activation failed';
      toast.error(`Activation failed: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.data) {
    const earnings = parseFloat(result?.commission?.activation_credit_zar || 0);
    return (
      <div className="space-y-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-center">
          <CheckCircle className="text-emerald-600 mx-auto mb-2" size={40} />
          <p className="font-semibold text-emerald-900">SIM Activated</p>
          <p className="text-xs text-emerald-700 mt-1">Hand the customer their SIM and the slip below.</p>
        </div>
        {/* Merchant-only commission line — hidden when the receipt is printed
            so the customer's PDF doesn't reveal what the merchant earned. */}
        {earnings > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-center print:hidden" data-testid="mojoair-earnings-line">
            <p className="text-sm font-semibold text-amber-900">Your earnings: R {earnings.toFixed(2)}</p>
            <p className="text-xs text-amber-700">Credited to your wallet</p>
          </div>
        )}
        <div className="bg-white border rounded p-4 text-sm space-y-2 font-mono">
          <div className="flex justify-between"><span className="text-gray-600">Customer:</span><span>{form.customerName}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">ID Number:</span><span>{form.customerIdNumber}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">MSISDN:</span><span className="font-bold" data-testid="mojoair-result-msisdn">{result.data.msisdn || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">ICCID:</span><span className="text-xs">{result.data.iccid || form.iccid}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">Status:</span><span className="font-bold text-emerald-700">{result.data.status || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">Subscription ID:</span><span className="text-xs">{result.data.subscriptionId || '—'}</span></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => window.print()}>Print receipt</Button>
          <Button
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => { setResult(null); setForm({ productId: form.productId, iccid: '', customerName: '', customerIdNumber: '', customerMsisdnContact: '' }); }}
          >
            Activate another SIM
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>SIM Package</Label>
        {loadingPackages ? (
          <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
            <Loader2 className="animate-spin" size={14} /> Loading packages…
          </div>
        ) : (
          <Select value={form.productId} onValueChange={(v) => setForm({ ...form, productId: v })}>
            <SelectTrigger data-testid="mojoair-package-select">
              <SelectValue placeholder="Pick a SIM package" />
            </SelectTrigger>
            <SelectContent>
              {packages.length === 0
                ? <div className="px-3 py-2 text-xs text-gray-500">No SIM-in-hand packages available</div>
                : packages.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.price > 0 ? ` — R ${parseFloat(p.price).toFixed(2)}` : ''}
                    </SelectItem>
                  ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div>
        <Label htmlFor="iccid">ICCID (from SIM card)</Label>
        <Input
          id="iccid"
          inputMode="numeric"
          autoComplete="off"
          placeholder="892707822... (18–22 digits)"
          value={form.iccid}
          onChange={(e) => setForm({ ...form, iccid: e.target.value })}
          data-testid="mojoair-iccid"
        />
        <p className="text-xs text-gray-500 mt-1">Scan or type the long number printed on the SIM card.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="cname">Customer Full Name</Label>
          <Input
            id="cname"
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
            data-testid="mojoair-customer-name"
          />
        </div>
        <div>
          <Label htmlFor="cidnum">Customer ID Number</Label>
          <Input
            id="cidnum"
            inputMode="numeric"
            value={form.customerIdNumber}
            onChange={(e) => setForm({ ...form, customerIdNumber: e.target.value })}
            placeholder="13-digit SA ID"
            data-testid="mojoair-customer-id"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="ccontact">Customer Contact Number (optional)</Label>
        <Input
          id="ccontact"
          inputMode="tel"
          value={form.customerMsisdnContact}
          onChange={(e) => setForm({ ...form, customerMsisdnContact: e.target.value })}
          placeholder="Existing number for SMS confirmation"
        />
      </div>
      <Button
        type="submit"
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
        disabled={submitting || !form.productId}
        data-testid="mojoair-activate-btn"
      >
        {submitting ? <><Loader2 className="animate-spin mr-2" size={16} /> Activating…</> : 'Activate SIM'}
      </Button>
    </form>
  );
};

// ── Top-up an existing Mojo Air MSISDN ─────────────────────────────────────
const MojoAirTopUp = ({ token, refreshData }) => {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [form, setForm] = useState({ msisdn: '', productId: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoadingProducts(true);
    axios
      .get(`${API}/limes/products?page=1&limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (!alive) return;
        const raw = r.data?.data || r.data?.items || [];
        // Limes catalog returns both a GUID (`id`) and a short numeric `sku`.
        // The order endpoint ONLY accepts the sku — anything else 400s with
        // 'does not identify a valid product'. Filter to rows that have a
        // numeric sku and a non-empty name so the dropdown doesn't surface
        // junk rows that would fail at submit-time.
        const items = (Array.isArray(raw) ? raw : []).filter(
          (p) => p && p.sku && /^\d+$/.test(String(p.sku)) && p.name
        );
        setProducts(items);
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail || e?.message;
        toast.error(`Failed to load Mojo Air catalog: ${typeof detail === 'string' ? detail : 'Unknown error'}`);
      })
      .finally(() => alive && setLoadingProducts(false));
    return () => { alive = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msisdn = form.msisdn.replace(/\D/g, '');
    if (!/^27\d{9}$/.test(msisdn)) return toast.error('MSISDN should be 11 digits starting with 27 (e.g. 27821234567)');
    if (!form.productId) return toast.error('Please pick a product');

    setSubmitting(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/limes/order`,
        { msisdn, products: [{ id: form.productId }] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(r.data);
      toast.success('Top-up successful');
      refreshData?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Top-up failed';
      const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail);
      // 404 + 'Subscriber does not exist' → guide the merchant
      if (detailStr.toLowerCase().includes('subscriber does not exist')) {
        toast.error('That number is not an active Mojo Air subscriber. Activate a SIM first.');
      } else {
        toast.error(`Top-up failed: ${detailStr}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const com = result?.commission || {};
    const sellerEarnings   = parseFloat(com.selling_merchant_credit || 0);
    const activatorPayout  = parseFloat(com.activator_credit || 0);
    const orderTotal       = parseFloat(com.order_amount_zar || 0);
    const ratePct          = parseFloat(com.rate_pct || 0);
    const classification   = com.classification || '';
    return (
      <div className="space-y-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-center">
          <CheckCircle className="text-emerald-600 mx-auto mb-2" size={40} />
          <p className="font-semibold text-emerald-900">Top-up Successful</p>
        </div>
        {/* Merchant-only commission lines — hidden on print so the customer's
            PDF doesn't reveal what the merchant or the activating merchant earned. */}
        {sellerEarnings > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-center print:hidden" data-testid="mojoair-topup-earnings-line">
            <p className="text-sm font-semibold text-amber-900">
              Your commission: R {sellerEarnings.toFixed(2)}
            </p>
            <p className="text-xs text-amber-700">
              {ratePct.toFixed(1)}% of R {orderTotal.toFixed(2)}{classification ? ` (${classification})` : ''}
            </p>
          </div>
        )}
        {activatorPayout > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center text-xs text-blue-800 print:hidden">
            R {activatorPayout.toFixed(2)} ongoing commission also paid to the merchant who activated this SIM.
          </div>
        )}
        <div className="bg-white border rounded p-4 text-sm space-y-2 font-mono">
          <div className="flex justify-between"><span className="text-gray-600">MSISDN:</span><span className="font-bold">{form.msisdn}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">Order ID:</span><span className="text-xs">{result?.data?.orderId || result?.orderId || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">State:</span><span>{result?.data?.orderState || result?.orderState || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">Transaction:</span><span className="text-xs">{result?.data?.transactionId || result?.transactionId || '—'}</span></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => window.print()}>Print receipt</Button>
          <Button
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => { setResult(null); setForm({ msisdn: '', productId: form.productId }); }}
          >
            New top-up
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="topup-msisdn">Customer's Mojo Air MSISDN</Label>
        <Input
          id="topup-msisdn"
          inputMode="tel"
          placeholder="27821234567"
          value={form.msisdn}
          onChange={(e) => setForm({ ...form, msisdn: e.target.value })}
          data-testid="mojoair-topup-msisdn"
        />
        <p className="text-xs text-gray-500 mt-1">Enter the number as 11 digits starting with 27.</p>
      </div>
      <div>
        <Label>Airtime / Data Product</Label>
        {loadingProducts ? (
          <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
            <Loader2 className="animate-spin" size={14} /> Loading catalog…
          </div>
        ) : (
          <Select value={form.productId} onValueChange={(v) => setForm({ ...form, productId: v })}>
            <SelectTrigger data-testid="mojoair-product-select">
              <SelectValue placeholder="Pick a product" />
            </SelectTrigger>
            <SelectContent>
              {products.length === 0
                ? <div className="px-3 py-2 text-xs text-gray-500">No products available</div>
                : products.map((p) => (
                    <SelectItem key={p.sku} value={p.sku}>
                      {p.name}{p.price > 0 ? ` — R ${parseFloat(p.price).toFixed(2)}` : ''}
                    </SelectItem>
                  ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <Button
        type="submit"
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
        disabled={submitting || !form.productId}
        data-testid="mojoair-topup-btn"
      >
        {submitting ? <><Loader2 className="animate-spin mr-2" size={16} /> Loading…</> : 'Load Top-up'}
      </Button>
    </form>
  );
};

export default MerchantDashboard;
